/**
 * ver 2.0.0
 * www/system/system/javascript-src/_TimePickerForDateTimeModV2.js
 * TsTimePicker_dateTimeMod (DateTime mod) - Vanilla JavaScript time picker with form inputs
 * and timezone display. Mimics the Ant Design Vue TimePicker look. In 12-hour mode the
 * control splits into hour (00-12; 00=12AM, 12=12PM), minute (00-59), and meridian
 * (AM/PM; click toggles; A/a and P/p when focused). Supports HourName/HourValue,
 * MinuteName/MinuteValue, AMPMName/AMPMValue hidden fields, TimeZoneAbbr display,
 * and TimeOnChangeJS callback.
 *
 * Usage (with form inputs):
 *   const picker = new TsTimePicker_dateTimeMod({
 *     container: document.getElementById('time-picker-mount'),
 *     HourName: 'hourStartStandard',
 *     HourValue: '12',
 *     MinuteName: 'minuteStartStandard',
 *     MinuteValue: '0',
 *     AMPMName: 'ampmStartStandard',
 *     AMPMValue: 'AM',
 *     TimeZoneAbbr: 'PST',
 *     TimeOnChangeJS: 'onChangeStandard();',
 *     disabled: false,
 *     use12Hours: true
 *   });
 *
 * Work-week start (day + time, no timezone):
 *   const picker = new TsTimePicker_dateTimeMod({
 *     container: document.getElementById('time-picker-mount'),
 *     ShowDaysOfWeek: true,
 *     DaysOfWeekName: 'overtimeWeekDay',
 *     DaysOfWeekValue: '1',
 *     HourName: 'overtimeWeekHour',
 *     ...
 *   });
 *
 * Duration / time-length entry (e.g. PTO hours, no AM/PM or time zone):
 *   const picker = new TsTimePicker_dateTimeMod({
 *     container: document.getElementById('time-picker-mount'),
 *     HourName: 'hourEndNonStandard',
 *     HourValue: '8',
 *     MinuteName: 'minuteEndNonStandard',
 *     MinuteValue: '0',
 *     TimeOnChangeJS: 'onChangeNonStandard();',
 *     useDurationHours: true,
 *     use12Hours: false,
 *     showNowButton: false,
 *     showOkayButton: false
 *   });
 *   picker.setValue('17:30');
 *   const value = picker.getValue();
 *   picker.destroy();
 *
 * DevTools (dropdown stays open when the pointer leaves):
 *   picker.openPanelForDebug();  // returns panel element
 *   picker.closePanelForDebug();
 */

(function (global) {
  'use strict';

  const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  /** Duration picker: hours 00–24 (24 = full day; minutes forced to 00). */
  const HOURS_DURATION = Array.from({ length: 25 }, (_, i) => i);
  /** Minute dropdown: fixed list, not scrollable (matches calendar TsTimePicker.vue). */
  const MINUTES_10 = [0, 10, 20, 30, 40, 50];
  /** Fixed height (px) for each row in the minute (00, 10, … 50) column. */
  const MINUTE_TEN_ROW_HEIGHT_PX = 30;
  /** Minute list vertical padding: 3px top (align with hour column), 4px bottom. */
  const MINUTE_TEN_LIST_PADDING_TOP_PX = 3;
  const MINUTE_TEN_LIST_PADDING_BOTTOM_PX = 4;
  /** Dwell time (ms) on the trigger before opening the dropdown via hover */
  const HOVER_OPEN_DELAY_MS = 250;
  /** Fixed width (px) for each hour/minute dropdown column (80% of 75px baseline). */
  const HOUR_MINUTE_COLUMN_WIDTH_PX = 60;
  /** Delay before hiding the slide-out minute grid after pointer leaves row/subpop (cross-gap travel). */
  const MINUTE_SUBPOP_HIDE_DELAY_MS = 500;
  /** Delay after pointer leaves the dropdown before closing (allows travel to inputs/subpop). */
  const PANEL_MOUSE_LEAVE_CLOSE_DELAY_MS = 500;
  /** Horizontal gap (px) between the minute column and the slide-out picker */
  const MINUTE_SUBPOP_SIDE_GAP_PX = 10;
  /** Day-of-week values stored in form fields (1 = Sunday … 7 = Saturday). */
  const DAYS_OF_WEEK_NUMS = [1, 2, 3, 4, 5, 6, 7];
  /** i18n keys for day abbreviations (Sun–Sat). */
  const DAY_I18N_KEYS = [
    'JavaScript.Day.SUN',
    'JavaScript.Day.MON',
    'JavaScript.Day.TUE',
    'JavaScript.Day.WED',
    'JavaScript.Day.THU',
    'JavaScript.Day.FRI',
    'JavaScript.Day.SAT'
  ];
  /** English fallbacks when getTranslation is unavailable. */
  const DAY_FALLBACK_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  /** Day-of-week dropdown column width (px). */
  const DAY_COLUMN_WIDTH_PX = 70;
  /** Horizontal inset on day rows so hover highlight width matches the prior 60px column. */
  const DAY_COLUMN_HIGHLIGHT_MARGIN_PX = 5;
  /** Day-of-week panel column height (px); hour and minute columns share this. */
  const DOW_PANEL_COLUMN_HEIGHT_PX = 224;
  /** Day-of-week day column outer height (px). */
  const DOW_DAY_COLUMN_HEIGHT_PX = 231;
  /** Day-of-week day column bottom padding (px). */
  const DOW_DAY_COLUMN_PADDING_BOTTOM_PX = 6;
  /** Day-of-week trigger input wrap right padding (px). */
  const DOW_INPUT_WRAP_PADDING_RIGHT_PX = 13;
  /** Day-of-week rows in the day column (SUN–SAT); hour rows use the same height for alignment. */
  const DOW_DAY_ROW_COUNT = 7;
  /** Vertical padding on day/hour lists (4px top + 4px bottom). */
  const DOW_LIST_VERTICAL_PADDING_PX = 8;
  const DOW_DAY_ROW_HEIGHT_CALC =
    'calc((' + DOW_DAY_COLUMN_HEIGHT_PX + 'px - ' + DOW_DAY_COLUMN_PADDING_BOTTOM_PX + 'px - ' + DOW_LIST_VERTICAL_PADDING_PX + 'px) / ' + DOW_DAY_ROW_COUNT + ')';
  const DOW_HOUR_ROW_HEIGHT_CALC =
    'calc((' + DOW_PANEL_COLUMN_HEIGHT_PX + 'px - ' + DOW_LIST_VERTICAL_PADDING_PX + 'px) / ' + DOW_DAY_ROW_COUNT + ')';

  /**
   * Position minute subpop near hovered decade row; open left if viewport space is tight.
   * @param {HTMLElement} outerEl
   * @param {HTMLElement} rowLi
   * @param {HTMLElement} subpopEl
   */
  function positionMinuteSubpop(outerEl, rowLi, subpopEl) {
    var or = outerEl.getBoundingClientRect();
    var lr = rowLi.getBoundingClientRect();
    subpopEl.style.display = 'block';
    var spW = subpopEl.offsetWidth;
    var spH = subpopEl.offsetHeight;
    var margin = MINUTE_SUBPOP_SIDE_GAP_PX;
    var viewportPad = 8;
    var fitsRight = or.right + margin + spW <= window.innerWidth - viewportPad;
    var openLeft = !fitsRight;
    var left;
    if (openLeft) {
      left = or.left - margin - spW;
      if (left < viewportPad) left = viewportPad;
    } else {
      left = or.right + margin;
      if (left + spW > window.innerWidth - viewportPad) {
        left = Math.max(viewportPad, window.innerWidth - viewportPad - spW);
      }
    }
    var top = lr.top + lr.height / 2 - spH / 2;
    var clampedTop = Math.max(viewportPad, Math.min(top, window.innerHeight - viewportPad - spH));
    subpopEl.style.position = 'fixed';
    subpopEl.style.left = left + 'px';
    subpopEl.style.top = clampedTop + 'px';
    subpopEl.classList.toggle('ts-minute-subpop--arrow-left', !openLeft);
    subpopEl.classList.toggle('ts-minute-subpop--arrow-right', openLeft);
  }

  /**
   * Parse hour/minute/AMPM form values (12h) to { hours24, hours12, minutes, isPM }
   * HourValue: "12" + AM = midnight; "12" + PM = noon; "1"–"11"; "0"/"00" normalize to clock 12.
   */
  function parseTimeFromForm(hourVal, minuteVal, ampmVal, minuteStep) {
    var h = hourVal === '' ? 12 : parseInt(hourVal, 10);
    if (isNaN(h) || h < 0 || h > 12) h = 12;
    var hours12 = h === 0 ? 12 : h;
    var minutes = minuteVal === '' ? 0 : parseInt(minuteVal, 10);
    if (isNaN(minutes) || minutes < 0 || minutes > 59) minutes = 0;
    if (minuteStep === 5) {
      minutes = Math.round(minutes / 5) * 5;
      if (minutes === 60) minutes = 55;
    }
    var isPM = (ampmVal + '').toUpperCase() === 'PM';
    var hours24 = isPM
      ? (hours12 === 12 ? 12 : hours12 + 12)
      : (hours12 === 12 ? 0 : hours12);
    return { hours24: hours24, hours12: hours12, minutes: minutes, isPM: isPM };
  }

  /**
   * Parse "HH:mm" (24h) to { hours24, hours12, minutes, isPM }
   * @param {string|Date} value
   * @param {number} minuteStep - 1 = all 60 minutes, 5 = 5-minute steps
   */
  function parseTime(value, minuteStep) {
    var snapToStep = minuteStep === 5;
    if (!value) return { hours24: 9, hours12: 9, minutes: 0, isPM: false };
    if (value instanceof Date) {
      var minutes = value.getMinutes();
      if (snapToStep) {
        minutes = Math.round(minutes / 5) * 5;
        if (minutes === 60) minutes = 55;
      } else {
        minutes = Math.min(59, minutes);
      }
      return {
        hours24: value.getHours(),
        hours12: value.getHours() % 12 || 12,
        minutes: minutes,
        isPM: value.getHours() >= 12
      };
    }
    var str = String(value).trim();
    var match = str.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return { hours24: 9, hours12: 9, minutes: 0, isPM: false };
    var hours24 = Math.min(23, Math.max(0, parseInt(match[1], 10)));
    var minutes = Math.min(59, Math.max(0, parseInt(match[2], 10)));
    if (snapToStep) {
      minutes = Math.round(minutes / 5) * 5;
      if (minutes === 60) minutes = 55;
    }
    var hours12 = hours24 % 12 || 12;
    return { hours24: hours24, hours12: hours12, minutes: minutes, isPM: hours24 >= 12 };
  }

  /**
   * Format { hours24, minutes } to "HH:mm"
   */
  function toHHmm(hours24, minutes) {
    const h = String(hours24).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');
    return h + ':' + m;
  }

  /**
   * Format for display: "h:mm A" (12-hour)
   */
  function format12h(hours24, minutes) {
    const hours12 = hours24 % 12 || 12;
    const m = String(minutes).padStart(2, '0');
    const ampm = hours24 >= 12 ? 'PM' : 'AM';
    return hours12 + ':' + m + ' ' + ampm;
  }

  /**
   * 12h hour field for form + display: 12 = 12 AM or 12 PM on the clock; 01–11 otherwise.
   * Hidden hour submits 12 + AM/PM; server maps 12+AM to 0, 12+PM to 12.
   */
  function formatHourDisplay12(state) {
    var h12 = state.hours12;
    if (h12 === 12) return '12';
    return String(h12).padStart(2, '0');
  }

  /**
   * Duration hour field display: 00–24 (zero-padded).
   */
  function formatHourDisplayDuration(hours24) {
    return String(hours24).padStart(2, '0');
  }

  /**
   * Parse day-of-week form value (1–7). Defaults to 1 (Sunday).
   * @param {string|number} value
   * @returns {number}
   */
  function parseDayOfWeekValue(value) {
    var n = parseInt(value, 10);
    if (isNaN(n) || n < 1 || n > 7) return 1;
    return n;
  }

  /**
   * Resolve a translated string, or return fallback when i18n is unavailable.
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  function translateOrFallback(key, fallback) {
    if (typeof getTranslation === 'function') {
      return getTranslation(key);
    }
    return fallback;
  }

  /**
   * Fetch the user's current local time from the server (not browser clock).
   * @param {function(Error|null, object|null): void} callback
   */
  function fetchServerUserTime(callback) {
    var baseUrl = (global.g && global.g.baseApiUrl) ? global.g.baseApiUrl : '/api/private/v1';
    var url = baseUrl + '/server/time';
    var headers = {};
    if (typeof createBaseApiHeaders === 'function' &&
        typeof getApiKeyFromStorage === 'function' &&
        typeof getApiTokenFromStorage === 'function') {
      headers = createBaseApiHeaders(getApiKeyFromStorage(), getApiTokenFromStorage());
    }
    fetch(url, {
      method: 'GET',
      cache: 'no-store',
      headers: headers
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.json();
      })
      .then(function (data) {
        if (data && data.USERTIME) {
          callback(null, data.USERTIME);
        } else {
          callback(new Error('Missing USERTIME in response'));
        }
      })
      .catch(function (err) {
        callback(err);
      });
  }

  /**
   * Localized 3-letter day label for display (falls back to English abbreviations).
   * @param {TsTimePicker_dateTimeMod} picker
   * @param {number} dayNum 1–7
   * @returns {string}
   */
  function getDayLabel(picker, dayNum) {
    var idx = parseDayOfWeekValue(dayNum) - 1;
    if (picker.daysOfWeekLabels && picker.daysOfWeekLabels[idx]) {
      return picker.daysOfWeekLabels[idx];
    }
    return translateOrFallback(DAY_I18N_KEYS[idx], DAY_FALLBACK_LABELS[idx] || 'SUN');
  }

  /**
   * Parse duration "HH:mm" or form values (hours 0–24; hour 24 => minutes 0).
   */
  function parseDurationTime(value, minuteStep) {
    if (!value) return { hours24: 8, hours12: 8, minutes: 0, isPM: false };
    if (value instanceof Date) {
      var h = Math.min(24, value.getHours());
      var minutes = h === 24 ? 0 : value.getMinutes();
      if (minuteStep === 5 && h < 24) {
        minutes = Math.round(minutes / 5) * 5;
        if (minutes === 60) minutes = 55;
      }
      return { hours24: h, hours12: h % 12 || 12, minutes: minutes, isPM: false };
    }
    var str = String(value).trim();
    var match = str.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return { hours24: 8, hours12: 8, minutes: 0, isPM: false };
    var hours24 = Math.min(24, Math.max(0, parseInt(match[1], 10)));
    if (hours24 === 24) {
      return { hours24: 24, hours12: 12, minutes: 0, isPM: false };
    }
    var minutes = Math.min(59, Math.max(0, parseInt(match[2], 10)));
    if (minuteStep === 5) {
      minutes = Math.round(minutes / 5) * 5;
      if (minutes === 60) minutes = 55;
    }
    return { hours24: hours24, hours12: hours24 % 12 || 12, minutes: minutes, isPM: false };
  }

  function parseDurationTimeFromForm(hourVal, minuteVal, minuteStep) {
    var h = hourVal === '' ? 0 : parseInt(hourVal, 10);
    if (isNaN(h) || h < 0 || h > 24) h = 0;
    if (h === 24) {
      return { hours24: 24, hours12: 12, minutes: 0, isPM: false };
    }
    var minutes = minuteVal === '' ? 0 : parseInt(minuteVal, 10);
    if (isNaN(minutes) || minutes < 0 || minutes > 59) minutes = 0;
    if (minuteStep === 5) {
      minutes = Math.round(minutes / 5) * 5;
      if (minutes === 60) minutes = 55;
    }
    return { hours24: h, hours12: h % 12 || 12, minutes: minutes, isPM: false };
  }

  function durationDigitsToHours(digits) {
    var s = String(digits == null ? '' : digits).replace(/\D/g, '');
    if (s === '') return null;
    var n = parseInt(s, 10);
    if (isNaN(n) || n < 0 || n > 24) return null;
    return n;
  }

  function stateFromDurationSplitInputs(hourDigits, minuteDigits, minuteStep, prevState) {
    var hours24 = durationDigitsToHours(hourDigits);
    if (hours24 === null) hours24 = prevState.hours24;

    var mRaw = String(minuteDigits == null ? '' : minuteDigits).replace(/\D/g, '');
    var minutes = mRaw === '' ? prevState.minutes : parseInt(mRaw, 10);
    if (isNaN(minutes) || minutes < 0 || minutes > 59) minutes = prevState.minutes;

    if (hours24 === 24) {
      minutes = 0;
    } else if (minuteStep === 5) {
      minutes = Math.round(minutes / 5) * 5;
      if (minutes === 60) minutes = 55;
    }

    return { hours24: hours24, hours12: hours24 % 12 || 12, minutes: minutes, isPM: false };
  }

  /**
   * Parse hour field digits (00–12) to clock hours12 (1–12).
   */
  function hourDigitsToHours12(digits) {
    var s = String(digits == null ? '' : digits).replace(/\D/g, '');
    if (s === '') return null;
    var n = parseInt(s, 10);
    if (isNaN(n) || n < 0 || n > 12) return null;
    if (n === 0 || n === 12) return 12;
    return n;
  }

  /**
   * Build state from split inputs; preserves meridian when hour/minute blank on blur.
   */
  function stateFromSplitInputs(hourDigits, minuteDigits, isPM, minuteStep, prevState) {
    var hours12 = hourDigitsToHours12(hourDigits);
    if (hours12 === null) hours12 = prevState.hours12;

    var mRaw = String(minuteDigits == null ? '' : minuteDigits).replace(/\D/g, '');
    var minutes = mRaw === '' ? prevState.minutes : parseInt(mRaw, 10);
    if (isNaN(minutes) || minutes < 0 || minutes > 59) minutes = prevState.minutes;

    if (minuteStep === 5) {
      minutes = Math.round(minutes / 5) * 5;
      if (minutes === 60) minutes = 55;
    }

    var hours24 = isPM
      ? (hours12 === 12 ? 12 : hours12 + 12)
      : (hours12 === 12 ? 0 : hours12);
    return { hours24: hours24, hours12: hours12, minutes: minutes, isPM: isPM };
  }

  /**
   * Keep only allowed typed characters for the input: 0-9, :, A/P/M (auto-uppercase).
   */
  function sanitizeTypedTimeInput(raw) {
    var out = '';
    var src = String(raw == null ? '' : raw);
    for (var i = 0; i < src.length; i++) {
      var ch = src.charAt(i);
      if ((ch >= '0' && ch <= '9') || ch === ':') {
        out += ch;
      } else if (ch === 'a' || ch === 'A' || ch === 'p' || ch === 'P' || ch === 'm' || ch === 'M') {
        out += ch.toUpperCase();
      }
    }
    return out;
  }

  /**
   * Parse typed input into picker state.
   * Supports 12h (e.g. 9:30AM, 9:30A, 9:30) and 24h (e.g. 21:30, 9:30).
   */
  function parseTypedTimeInput(value, use12Hours, minuteStep, currentState, useDurationHours) {
    var cleaned = sanitizeTypedTimeInput(value).replace(/\s+/g, '');
    if (!cleaned) return null;

    var h;
    var m;
    var meridian;
    var match;

    if (useDurationHours) {
      match = cleaned.match(/^(\d{1,2})(?::?(\d{1,2}))?$/);
      if (!match) return null;
      h = parseInt(match[1], 10);
      m = match[2] === undefined || match[2] === '' ? 0 : parseInt(match[2], 10);
      if (isNaN(h) || h < 0 || h > 24 || isNaN(m) || m < 0 || m > 59) return null;
      if (h === 24) m = 0;
      else if (minuteStep === 5) {
        m = Math.round(m / 5) * 5;
        if (m === 60) m = 55;
      }
      return { hours24: h, hours12: h % 12 || 12, minutes: m, isPM: false };
    }

    if (use12Hours) {
      match = cleaned.match(/^(\d{1,2})(?::?(\d{1,2}))?([AP]M?)?$/);
      if (!match) return null;
      h = parseInt(match[1], 10);
      m = match[2] === undefined || match[2] === '' ? 0 : parseInt(match[2], 10);
      meridian = (match[3] || '').toUpperCase();
      if (meridian === 'A') meridian = 'AM';
      if (meridian === 'P') meridian = 'PM';
      if (h < 1 || h > 12 || isNaN(m) || m < 0 || m > 59) return null;

      if (minuteStep === 5) {
        m = Math.round(m / 5) * 5;
        if (m === 60) m = 55;
      }

      var isPM = meridian ? meridian === 'PM' : !!(currentState && currentState.isPM);
      var hours24 = isPM ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
      return {
        hours24: hours24,
        hours12: h,
        minutes: m,
        isPM: isPM
      };
    }

    match = cleaned.match(/^(\d{1,2})(?::?(\d{1,2}))?$/);
    if (!match) return null;
    h = parseInt(match[1], 10);
    m = match[2] === undefined || match[2] === '' ? 0 : parseInt(match[2], 10);
    if (isNaN(h) || h < 0 || h > 23 || isNaN(m) || m < 0 || m > 59) return null;

    if (minuteStep === 5) {
      m = Math.round(m / 5) * 5;
      if (m === 60) m = 55;
    }

    return {
      hours24: h,
      hours12: h % 12 || 12,
      minutes: m,
      isPM: h >= 12
    };
  }

  /**
   * Inject default styles so picker works without Ant Design CSS
   */
  function injectStyles() {
    const panelFontOverride = `
      .ant-time-picker-panel-combobox { font-size: 18px !important; }
      .ant-time-picker-panel-select li { font-size: 18px !important; }
      .ant-time-picker-panel-select[data-type="hour"] li { text-align: center !important; }
    `;
    const selectedNoFillHoverFixMarker = '/*ts-time-picker-selected-nohighlight-hoverfix-v1*/';
    const minuteTenSpaceBetweenMarker = '/*ts-minute-ten-space-between-v1*/';
    const minuteTenSpaceBetweenCss = `
      ${minuteTenSpaceBetweenMarker}
      .ant-time-picker-panel-select[data-type="minute"].ts-minute-ten-outer {
        min-height: 200px !important;
        max-height: 200px !important;
        display: flex !important;
        flex-direction: column !important;
        box-sizing: border-box !important;
      }
      .ts-minute-ten-scroll {
        flex: 1 1 auto !important;
        min-height: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        overflow-y: visible !important;
        overflow-x: visible !important;
      }
      .ts-minute-ten-scroll ul.ts-minute-ten-list {
        flex: 1 1 auto !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: space-between !important;
        padding: ${MINUTE_TEN_LIST_PADDING_TOP_PX}px 0 ${MINUTE_TEN_LIST_PADDING_BOTTOM_PX}px 0 !important;
        margin: 0 !important;
      }
    `;
    const noSelectedHighlightCss = `
      ${selectedNoFillHoverFixMarker}
      .ant-time-picker-panel-select[data-type="hour"] li.ant-time-picker-panel-cell-selected,
      .ant-time-picker-panel-select[data-type="minute"] li.ant-time-picker-panel-cell-selected {
        background-color: transparent !important;
      }
      /* :hover must win over transparent !important above (same specificity loses without !important). */
      .ant-time-picker-panel-select[data-type="hour"] li.ant-time-picker-panel-cell-selected:hover,
      .ant-time-picker-panel-select[data-type="minute"] li.ant-time-picker-panel-cell-selected:hover {
        background-color: #dff0fb !important;
      }
    `;
    const existing = document.getElementById('ts-time-picker-styles');
    if (existing) {
      if (existing.textContent.indexOf('.ant-time-picker-panel-combobox { font-size: 18px !important; }') === -1) {
        existing.textContent += panelFontOverride;
      }
      const minuteNoScroll = '.ant-time-picker-panel-select[data-type="minute"]:not(.ts-minute-ten-outer) { max-height: none; overflow-y: visible; }';
      if (existing.textContent.indexOf('[data-type="minute"]:not(.ts-minute-ten-outer)') === -1) {
        existing.textContent += minuteNoScroll;
      }
      const minutePopoutCss = `
        .ant-time-picker-panel-select.ts-minute-ten-outer { overflow: visible; position: relative; min-height: 200px; max-height: 200px; display: flex; flex-direction: column; box-sizing: border-box; }
        .ts-minute-ten-scroll { flex: 1 1 auto; min-height: 0; overflow-y: visible; overflow-x: visible; display: flex; flex-direction: column; }
        .ts-minute-ten-scroll ul.ts-minute-ten-list { flex: 1 1 auto; display: flex; flex-direction: column; justify-content: space-between; width: 100% !important; box-sizing: border-box; padding: ${MINUTE_TEN_LIST_PADDING_TOP_PX}px 0 ${MINUTE_TEN_LIST_PADDING_BOTTOM_PX}px 0; margin: 0; list-style: none; }
        .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 4px 2px 4px 6px; }
        .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row::after {
          content: '';
          flex-shrink: 0;
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 4px 0 4px 6px;
          border-color: transparent transparent transparent #bfbfbf;
          opacity: 0.9;
        }
        .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row:hover::after { border-left-color: #8c8c8c; }
        .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row.ant-time-picker-panel-cell-selected::after { border-left-color: #bfbfbf; opacity: 0.9; }
        .ts-minute-subpop {
          display: none;
          z-index: 10060;
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 3px;
          box-shadow: 0 2px 10px rgba(0,0,0,.18);
          padding: 10px 12px;
          box-sizing: border-box;
          pointer-events: auto;
          overflow: visible;
        }
        .ts-minute-subpop-inner { display: flex; flex-direction: column; gap: 8px; align-items: stretch; }
        .ts-minute-subpop-row { display: flex; flex-wrap: nowrap; gap: 6px; justify-content: center; }
        .ts-minute-subpop-cell {
          min-width: 30px;
          text-align: center;
          cursor: pointer;
          font-size: 22px;
          padding: 4px 1px;
          border-radius: 2px;
          color: inherit;
          font-weight: inherit;
          line-height: 1.2;
          user-select: none;
        }
        .ts-minute-subpop-cell:hover { background-color: #dff0fb; border-radius: 4px; }
        .ts-minute-subpop-cell.ts-minute-subpop-cell-selected { color: inherit; font-weight: inherit; }
        .ts-minute-subpop.ts-minute-subpop--arrow-left::before {
          content: '';
          position: absolute;
          right: 100%;
          top: 50%;
          margin-right: -1px;
          transform: translateY(-50%);
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 8px 10px 8px 0;
          border-color: transparent #fff transparent transparent;
          z-index: 2;
          pointer-events: none;
        }
        .ts-minute-subpop.ts-minute-subpop--arrow-right::after {
          content: '';
          position: absolute;
          left: 100%;
          top: 50%;
          margin-left: 1px;
          transform: translateY(-50%);
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 8px 0 8px 10px;
          border-color: transparent transparent transparent #e8e8e8;
          pointer-events: none;
        }
      `;
      if (existing.textContent.indexOf('.ts-minute-subpop') === -1) {
        existing.textContent += minutePopoutCss;
      }
      if (existing.textContent.indexOf(selectedNoFillHoverFixMarker) === -1) {
        existing.textContent += noSelectedHighlightCss;
      }
      if (existing.textContent.indexOf(minuteTenSpaceBetweenMarker) === -1) {
        existing.textContent += minuteTenSpaceBetweenCss;
      }
      const hourDowColumnHeightV1Marker = '/*ts-time-picker-datetime-mod-hour-dow-column-height-v1*/';
      if (existing.textContent.indexOf(hourDowColumnHeightV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          hourDowColumnHeightV1Marker +
          '\n    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="hour"] {\n' +
          '      min-height: ' + DOW_PANEL_COLUMN_HEIGHT_PX + 'px !important;\n' +
          '      max-height: ' + DOW_PANEL_COLUMN_HEIGHT_PX + 'px !important;\n' +
          '      box-sizing: border-box !important;\n' +
          '    }\n';
      }
      const hourDowColumnHeightV2Marker = '/*ts-time-picker-datetime-mod-hour-dow-column-height-v2*/';
      if (existing.textContent.indexOf(hourDowColumnHeightV2Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          hourDowColumnHeightV2Marker +
          '\n    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="hour"] {\n' +
          '      min-height: ' + DOW_PANEL_COLUMN_HEIGHT_PX + 'px !important;\n' +
          '      max-height: ' + DOW_PANEL_COLUMN_HEIGHT_PX + 'px !important;\n' +
          '    }\n';
      }
      const dowRowSpacingV1Marker = '/*ts-time-picker-datetime-mod-dow-row-spacing-v1*/';
      if (existing.textContent.indexOf(dowRowSpacingV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          dowRowSpacingV1Marker +
          '\n    .ts-time-picker-with-dow .ant-time-picker-panel-select.ts-minute-ten-outer {\n' +
          '      min-height: ' + DOW_PANEL_COLUMN_HEIGHT_PX + 'px !important;\n' +
          '      max-height: ' + DOW_PANEL_COLUMN_HEIGHT_PX + 'px !important;\n' +
          '    }\n' +
          '    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"] {\n' +
          '      min-height: ' + DOW_DAY_COLUMN_HEIGHT_PX + 'px !important;\n' +
          '      max-height: ' + DOW_DAY_COLUMN_HEIGHT_PX + 'px !important;\n' +
          '      padding-bottom: ' + DOW_DAY_COLUMN_PADDING_BOTTOM_PX + 'px !important;\n' +
          '      display: flex !important;\n' +
          '      flex-direction: column !important;\n' +
          '      box-sizing: border-box !important;\n' +
          '    }\n' +
          '    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"] ul {\n' +
          '      flex: 1 1 auto !important;\n' +
          '      min-height: 0 !important;\n' +
          '      display: flex !important;\n' +
          '      flex-direction: column !important;\n' +
          '      justify-content: space-between !important;\n' +
          '      padding: 4px 0 !important;\n' +
          '      margin: 0 !important;\n' +
          '      width: 100% !important;\n' +
          '      box-sizing: border-box !important;\n' +
          '    }\n' +
          '    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"] li,\n' +
          '    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="hour"] li {\n' +
          '      padding: 4px 2px 4px 6px !important;\n' +
          '      margin: 1px 8px !important;\n' +
          '      line-height: 1.2 !important;\n' +
          '      box-sizing: border-box !important;\n' +
          '    }\n' +
          '    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="hour"] ul {\n' +
          '      padding: 4px 0 !important;\n' +
          '    }\n';
      }
      const dowDayHourAlignV1Marker = '/*ts-time-picker-datetime-mod-dow-day-hour-align-v1*/';
      if (existing.textContent.indexOf(dowDayHourAlignV1Marker) === -1) {
        var dowRowHeightCalc =
          'calc((' + DOW_PANEL_COLUMN_HEIGHT_PX + 'px - ' + DOW_LIST_VERTICAL_PADDING_PX + 'px) / ' + DOW_DAY_ROW_COUNT + ')';
        existing.textContent +=
          '\n    ' +
          dowDayHourAlignV1Marker +
          '\n    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"] ul {\n' +
          '      display: block !important;\n' +
          '      flex: none !important;\n' +
          '      justify-content: flex-start !important;\n' +
          '      padding: 4px 0 !important;\n' +
          '      margin: 0 !important;\n' +
          '    }\n' +
          '    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"] li,\n' +
          '    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="hour"] li {\n' +
          '      height: ' + dowRowHeightCalc + ' !important;\n' +
          '      min-height: ' + dowRowHeightCalc + ' !important;\n' +
          '      display: flex !important;\n' +
          '      align-items: center !important;\n' +
          '      justify-content: center !important;\n' +
          '      padding: 0 !important;\n' +
          '      margin: 1px 8px !important;\n' +
          '      line-height: 1.2 !important;\n' +
          '      box-sizing: border-box !important;\n' +
          '    }\n';
      }
      const dowMinuteHourAlignV1Marker = '/*ts-time-picker-datetime-mod-dow-minute-hour-align-v1*/';
      if (existing.textContent.indexOf(dowMinuteHourAlignV1Marker) === -1) {
        var dowMinuteRowHeightCalc =
          'calc((' + DOW_PANEL_COLUMN_HEIGHT_PX + 'px - ' + DOW_LIST_VERTICAL_PADDING_PX + 'px) / ' + DOW_DAY_ROW_COUNT + ')';
        existing.textContent +=
          '\n    ' +
          dowMinuteHourAlignV1Marker +
          '\n    .ts-time-picker-with-dow .ts-minute-ten-scroll ul.ts-minute-ten-list {\n' +
          '      justify-content: flex-start !important;\n' +
          '      padding-top: ' + MINUTE_TEN_LIST_PADDING_TOP_PX + 'px !important;\n' +
          '      padding-bottom: ' + MINUTE_TEN_LIST_PADDING_BOTTOM_PX + 'px !important;\n' +
          '    }\n' +
          '    .ts-time-picker-with-dow .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row {\n' +
          '      height: ' + dowMinuteRowHeightCalc + ' !important;\n' +
          '      min-height: ' + dowMinuteRowHeightCalc + ' !important;\n' +
          '      flex-shrink: 0 !important;\n' +
          '      padding: 0 !important;\n' +
          '      margin: 1px 8px !important;\n' +
          '      box-sizing: border-box !important;\n' +
          '    }\n';
      }
      const dowMinuteHourAlignV2Marker = '/*ts-time-picker-datetime-mod-dow-minute-hour-align-v2*/';
      if (existing.textContent.indexOf(dowMinuteHourAlignV2Marker) === -1) {
        var dowRowHeightCalcV2 =
          'calc((' + DOW_PANEL_COLUMN_HEIGHT_PX + 'px - ' + DOW_LIST_VERTICAL_PADDING_PX + 'px) / ' + DOW_DAY_ROW_COUNT + ')';
        existing.textContent +=
          '\n    ' +
          dowMinuteHourAlignV2Marker +
          '\n    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"] li,\n' +
          '    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="hour"] li,\n' +
          '    .ts-time-picker-with-dow .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row {\n' +
          '      height: ' + dowRowHeightCalcV2 + ' !important;\n' +
          '      min-height: ' + dowRowHeightCalcV2 + ' !important;\n' +
          '      display: flex !important;\n' +
          '      align-items: center !important;\n' +
          '      justify-content: center !important;\n' +
          '      padding: 0 !important;\n' +
          '      margin: 1px 8px !important;\n' +
          '      line-height: 1.2 !important;\n' +
          '      box-sizing: border-box !important;\n' +
          '      flex-shrink: 0 !important;\n' +
          '    }\n' +
          '    .ts-time-picker-with-dow .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row {\n' +
          '      gap: 4px !important;\n' +
          '    }\n' +
          '    .ts-time-picker-with-dow .ts-minute-ten-scroll ul.ts-minute-ten-list {\n' +
          '      justify-content: flex-start !important;\n' +
          '      padding-top: ' + MINUTE_TEN_LIST_PADDING_TOP_PX + 'px !important;\n' +
          '      padding-bottom: ' + MINUTE_TEN_LIST_PADDING_BOTTOM_PX + 'px !important;\n' +
          '    }\n';
      }
      const minuteTenRowHeightV1Marker = '/*ts-time-picker-datetime-mod-minute-ten-row-height-v1*/';
      if (existing.textContent.indexOf(minuteTenRowHeightV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          minuteTenRowHeightV1Marker +
          '\n    .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row {\n' +
          '      height: ' + MINUTE_TEN_ROW_HEIGHT_PX + 'px !important;\n' +
          '      min-height: ' + MINUTE_TEN_ROW_HEIGHT_PX + 'px !important;\n' +
          '      max-height: ' + MINUTE_TEN_ROW_HEIGHT_PX + 'px !important;\n' +
          '      padding: 0 6px 0 2px !important;\n' +
          '      box-sizing: border-box !important;\n' +
          '      flex-shrink: 0 !important;\n' +
          '    }\n' +
          '    .ts-time-picker-with-dow .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row {\n' +
          '      height: ' + MINUTE_TEN_ROW_HEIGHT_PX + 'px !important;\n' +
          '      min-height: ' + MINUTE_TEN_ROW_HEIGHT_PX + 'px !important;\n' +
          '      max-height: ' + MINUTE_TEN_ROW_HEIGHT_PX + 'px !important;\n' +
          '    }\n';
      }
      const minuteTenRowHeightV2Marker = '/*ts-time-picker-datetime-mod-minute-ten-row-height-v2*/';
      if (existing.textContent.indexOf(minuteTenRowHeightV2Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          minuteTenRowHeightV2Marker +
          '\n    .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row,\n' +
          '    .ts-time-picker-with-dow .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row {\n' +
          '      height: ' + MINUTE_TEN_ROW_HEIGHT_PX + 'px !important;\n' +
          '      min-height: ' + MINUTE_TEN_ROW_HEIGHT_PX + 'px !important;\n' +
          '      max-height: ' + MINUTE_TEN_ROW_HEIGHT_PX + 'px !important;\n' +
          '    }\n';
      }
      const minuteTenListPaddingV1Marker = '/*ts-time-picker-datetime-mod-minute-ten-list-padding-v1*/';
      if (existing.textContent.indexOf(minuteTenListPaddingV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          minuteTenListPaddingV1Marker +
          '\n    .ts-minute-ten-scroll ul.ts-minute-ten-list,\n' +
          '    .ts-time-picker-with-dow .ts-minute-ten-scroll ul.ts-minute-ten-list {\n' +
          '      padding-top: ' + MINUTE_TEN_LIST_PADDING_TOP_PX + 'px !important;\n' +
          '      padding-bottom: ' + MINUTE_TEN_LIST_PADDING_BOTTOM_PX + 'px !important;\n' +
          '    }\n';
      }
      const dowDayColumnSizingV1Marker = '/*ts-time-picker-datetime-mod-dow-day-column-sizing-v1*/';
      if (existing.textContent.indexOf(dowDayColumnSizingV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          dowDayColumnSizingV1Marker +
          '\n    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"],\n' +
          '    .ts-time-picker-with-dow .ant-time-picker-panel-select.ts-day-of-week-select {\n' +
          '      min-height: ' + DOW_DAY_COLUMN_HEIGHT_PX + 'px !important;\n' +
          '      max-height: ' + DOW_DAY_COLUMN_HEIGHT_PX + 'px !important;\n' +
          '      padding-bottom: ' + DOW_DAY_COLUMN_PADDING_BOTTOM_PX + 'px !important;\n' +
          '      box-sizing: border-box !important;\n' +
          '    }\n' +
          '    .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"] li {\n' +
          '      height: ' + DOW_DAY_ROW_HEIGHT_CALC + ' !important;\n' +
          '      min-height: ' + DOW_DAY_ROW_HEIGHT_CALC + ' !important;\n' +
          '    }\n';
      }
      const dowInputWrapPaddingV1Marker = '/*ts-time-picker-datetime-mod-dow-input-wrap-padding-v1*/';
      if (existing.textContent.indexOf(dowInputWrapPaddingV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          dowInputWrapPaddingV1Marker +
          '\n    .ts-time-picker-with-dow .ant-time-picker-input-wrap {\n' +
          '      padding-right: ' + DOW_INPUT_WRAP_PADDING_RIGHT_PX + 'px !important;\n' +
          '    }\n';
      }
      const hourScrollSnapDisabledV1Marker = '/*ts-time-picker-datetime-mod-hour-scroll-snap-disabled-v1*/';
      if (existing.textContent.indexOf(hourScrollSnapDisabledV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          hourScrollSnapDisabledV1Marker +
          '\n    .ant-time-picker-panel-select[data-type="hour"] {\n' +
          '      scroll-snap-type: none !important;\n' +
          '    }\n' +
          '    .ant-time-picker-panel-select[data-type="hour"] li {\n' +
          '      scroll-snap-align: none !important;\n' +
          '    }\n';
      }
      const hourLiMarginV1Marker = '/*ts-time-picker-datetime-mod-hour-li-margin-v1*/';
      if (existing.textContent.indexOf(hourLiMarginV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          hourLiMarginV1Marker +
          '\n    .ant-time-picker-panel-select[data-type="hour"] li {\n' +
          '      margin-top: 2px !important;\n' +
          '      margin-bottom: 2px !important;\n' +
          '    }\n';
      }
      const minuteSubpopTightSpacingV1Marker = '/*ts-time-picker-datetime-mod-minute-subpop-tight-spacing-v1*/';
      if (existing.textContent.indexOf(minuteSubpopTightSpacingV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          minuteSubpopTightSpacingV1Marker +
          '\n    .ts-minute-subpop-row {\n' +
          '      gap: 6px !important;\n' +
          '    }\n' +
          '    .ts-minute-subpop-cell {\n' +
          '      min-width: 30px !important;\n' +
          '      padding: 4px 1px !important;\n' +
          '    }\n';
      }
      const hourMinuteColumnWidthV1Marker = '/*ts-time-picker-datetime-mod-hour-minute-column-width-v1*/';
      if (existing.textContent.indexOf(hourMinuteColumnWidthV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          hourMinuteColumnWidthV1Marker +
          '\n    .ant-time-picker-panel-select[data-type="hour"],\n' +
          '    .ant-time-picker-panel-select[data-type="minute"] {\n' +
          '      flex: 0 0 ' + HOUR_MINUTE_COLUMN_WIDTH_PX + 'px !important;\n' +
          '      min-width: ' + HOUR_MINUTE_COLUMN_WIDTH_PX + 'px !important;\n' +
          '      max-width: ' + HOUR_MINUTE_COLUMN_WIDTH_PX + 'px !important;\n' +
          '    }\n' +
          '    .ant-time-picker-panel-select li {\n' +
          '      padding-left: 10px !important;\n' +
          '      padding-right: 10px !important;\n' +
          '    }\n' +
          '    .ant-time-picker-panel-select[data-type="hour"] li {\n' +
          '      margin-left: 8px !important;\n' +
          '      margin-right: 8px !important;\n' +
          '    }\n' +
          '    .ant-time-picker-panel-select[data-type="minute"] li {\n' +
          '      margin-left: 8px !important;\n' +
          '      margin-right: 8px !important;\n' +
          '    }\n';
      }
      const durationColumnWidthV1Marker = '/*ts-time-picker-datetime-mod-duration-column-width-v1*/';
      if (existing.textContent.indexOf(durationColumnWidthV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          durationColumnWidthV1Marker +
          '\n    .ant-time-picker-panel.ts-time-picker-duration-panel .ant-time-picker-panel-select[data-type="hour"],\n' +
          '    .ant-time-picker-panel.ts-time-picker-duration-panel .ant-time-picker-panel-select[data-type="minute"] {\n' +
          '      flex: 0 0 ' + HOUR_MINUTE_COLUMN_WIDTH_PX + 'px !important;\n' +
          '      min-width: ' + HOUR_MINUTE_COLUMN_WIDTH_PX + 'px !important;\n' +
          '      max-width: ' + HOUR_MINUTE_COLUMN_WIDTH_PX + 'px !important;\n' +
          '    }\n' +
          '    .ts-time-picker-duration .ts-time-picker-input-split .ts-time-picker-hour,\n' +
          '    .ts-time-picker-duration .ts-time-picker-input-split .ts-time-picker-minute {\n' +
          '      width: 21px !important;\n' +
          '    }\n';
      }
      const panelFitContentV1Marker = '/*ts-time-picker-datetime-mod-panel-fit-content-v1*/';
      if (existing.textContent.indexOf(panelFitContentV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          panelFitContentV1Marker +
          '\n    .ant-time-picker-panel { width: fit-content !important; min-width: 0 !important; }\n' +
          '    .ant-time-picker-panel-inner { width: fit-content !important; }\n' +
          '    .ant-time-picker-panel-combobox { width: fit-content !important; }\n' +
          '    .ant-time-picker-panel-select:not([data-is-ampm="true"]) { margin-top: 4px !important; border-right: none !important; }\n' +
          '    .ant-time-picker-panel-select:not([data-is-ampm="true"]):not(:last-child) { border-right: 1px solid #f0f0f0 !important; }\n';
      }
      const inputAlignV1Marker = '/*ts-time-picker-datetime-mod-input-align-v1*/';
      if (existing.textContent.indexOf(inputAlignV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          inputAlignV1Marker +
          '\n    .ts-time-picker-standalone .ts-time-picker-input-split { align-items: center !important; }\n' +
          '    .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-hm-hover-zone { align-items: center !important; }\n' +
          '    .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-hour,\n' +
          '    .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-minute {\n' +
          '      width: 21px !important;\n' +
          '      height: auto !important;\n' +
          '      min-height: 0 !important;\n' +
          '      padding: 0 !important;\n' +
          '      margin: 0 !important;\n' +
          '      overflow: visible !important;\n' +
          '      background: transparent !important;\n' +
          '      -webkit-appearance: none !important;\n' +
          '      appearance: none !important;\n' +
          '    }\n' +
          '    .ts-time-picker-duration .ts-time-picker-input-split .ts-time-picker-hour,\n' +
          '    .ts-time-picker-duration .ts-time-picker-input-split .ts-time-picker-minute {\n' +
          '      width: 21px !important;\n' +
          '    }\n' +
          '    .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-meridian {\n' +
          '      height: auto !important;\n' +
          '      min-height: 0 !important;\n' +
          '      max-height: none !important;\n' +
          '      padding: 0 0 0 4px !important;\n' +
          '      margin: 0 !important;\n' +
          '      display: inline-flex !important;\n' +
          '      align-items: center !important;\n' +
          '      line-height: 1.2 !important;\n' +
          '    }\n';
      }
      const inputWidthV1Marker = '/*ts-time-picker-datetime-mod-input-width-v1*/';
      if (existing.textContent.indexOf(inputWidthV1Marker) === -1) {
        existing.textContent +=
          '\n    ' +
          inputWidthV1Marker +
          '\n    .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-hour,\n' +
          '    .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-minute,\n' +
          '    .ts-time-picker-duration .ts-time-picker-input-split .ts-time-picker-hour,\n' +
          '    .ts-time-picker-duration .ts-time-picker-input-split .ts-time-picker-minute {\n' +
          '      width: 21px !important;\n' +
          '      max-width: none !important;\n' +
          '    }\n';
      }
      return;
    }
    const css = `
      .ts-time-picker-standalone.ant-time-picker { display: inline-block; width: auto; min-width: 0; }
      .ts-time-picker-standalone .ant-time-picker-input-wrap {
        display: inline-flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 2px;
        background-color: white;
        padding: 7px 8px 5px 6px;
        box-sizing: border-box;
        border: 1px solid #b6b6b6;
        border-radius: 3px;
        width: fit-content;
        max-width: 100%;
        }
      .ts-time-picker-with-dow .ant-time-picker-input-wrap {
        padding-right: ${DOW_INPUT_WRAP_PADDING_RIGHT_PX}px;
      }

      .ts-time-picker-standalone .ts-time-picker-input-split { display: flex; align-items: center; flex-wrap: nowrap; gap: 2px; }
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-hm-hover-zone {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 2px;
      }
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-hour,
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-minute {
        font-size: 18px;
        height: auto;
        min-height: 0;
        font-weight: bold;
        padding: 0;
        margin: 0;
        border-radius: 3px;
        width: 21px;
        max-width: none;
        text-align: center;
        font-family: inherit;
        color: #000;
        border: none;
        box-sizing: border-box;
        line-height: 1.2;
        overflow: visible;
        background: transparent;
        -webkit-appearance: none;
        appearance: none;
      }
      .ts-time-picker-duration .ts-time-picker-input-split .ts-time-picker-hour,
      .ts-time-picker-duration .ts-time-picker-input-split .ts-time-picker-minute {
        width: 21px;
      }
      .ant-time-picker-panel.ts-time-picker-duration-panel .ant-time-picker-panel-select[data-type="hour"],
      .ant-time-picker-panel.ts-time-picker-duration-panel .ant-time-picker-panel-select[data-type="minute"] {
        flex: 0 0 ${HOUR_MINUTE_COLUMN_WIDTH_PX}px;
        min-width: ${HOUR_MINUTE_COLUMN_WIDTH_PX}px;
        max-width: ${HOUR_MINUTE_COLUMN_WIDTH_PX}px;
      }
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-colon { font-size: 18px; font-weight: bold; user-select: none; padding: 0px; line-height: 1.2; }
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-meridian {
          font-size: 18px;
          font-weight: bold;
          padding: 0 0 0 4px;
          margin: 0;
          border-radius: 3px;
          max-width: 30px;
          font-family: inherit;
          color: #000;
          border: none;
          cursor: pointer;
          box-sizing: border-box;
          display: inline-flex;
          align-items: center;
          text-align: center;
          background: #fff;
          line-height: 1.2;
          height: auto;
        }
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-meridian:focus { outline: #0075b2 none 1px; }
      .ts-time-picker-datetime-mod .ts-time-picker-timezone-abbr {position: relative; top: 2px;}
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-day {
        font-size: 18px;
        font-weight: bold;
        min-width: 2.4em;
        text-align: center;
        user-select: none;
        padding: 0 4px 0 0;
        line-height: 1.2;
        color: #000;
      }
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-day.ts-time-picker-day-disabled {
        color: rgba(0,0,0,.25);
      }
      .ant-time-picker-panel-select[data-type="day"] {
        flex: 0 0 auto;
        min-width: ${DAY_COLUMN_WIDTH_PX - 8}px;
        max-width: ${DAY_COLUMN_WIDTH_PX}px;
        max-height: none;
        overflow-y: visible;
        border-right: 1px solid #f0f0f0;
      }
      .ant-time-picker-panel-select[data-type="day"] ul {
        padding: 0;
      }
      .ant-time-picker-panel-select[data-type="day"] li {
        padding: 6px 10px;
        text-align: center;
        border-radius: 10px;
        margin-left: ${DAY_COLUMN_HIGHLIGHT_MARGIN_PX}px;
        margin-right: ${DAY_COLUMN_HIGHLIGHT_MARGIN_PX}px;
        transition: background-color 120ms ease;
      }
      .ant-time-picker-panel-select[data-type="day"] li:hover {
        background-color: #dff0fb;
      }
      .ant-time-picker-panel-select[data-type="day"] li.ant-time-picker-panel-cell-selected {
        background-color: transparent;
      }
      .ant-time-picker-panel-select[data-type="day"] li.ant-time-picker-panel-cell-selected:hover {
        background-color: #dff0fb !important;
      }
      /* Day-of-week panel: column heights */
      .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="hour"],
      .ts-time-picker-with-dow .ant-time-picker-panel-select.ts-minute-ten-outer {
        min-height: ${DOW_PANEL_COLUMN_HEIGHT_PX}px;
        max-height: ${DOW_PANEL_COLUMN_HEIGHT_PX}px;
        box-sizing: border-box;
      }
      .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"],
      .ts-time-picker-with-dow .ant-time-picker-panel-select.ts-day-of-week-select {
        min-height: ${DOW_DAY_COLUMN_HEIGHT_PX}px;
        max-height: ${DOW_DAY_COLUMN_HEIGHT_PX}px;
        padding-bottom: ${DOW_DAY_COLUMN_PADDING_BOTTOM_PX}px;
        box-sizing: border-box;
        overflow-y: hidden;
      }
      .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"] ul {
        padding: 4px 0;
        margin: 0;
      }
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-hour:disabled,
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-minute:disabled,
      .ts-time-picker-standalone .ts-time-picker-input-split .ts-time-picker-meridian.ant-time-picker-input-disabled { color: rgba(0,0,0,.25); background: #f5f5f5; cursor: not-allowed; }
      .ts-time-picker-standalone .ant-time-picker-input { font-size: 18px; height: auto; min-height: 35px; font-weight:bold; padding: 5px 5px 5px 10px; border-radius: 3px; width: 131px; max-width: 131px; font-family: inherit; color: #000; border: 1px solid #d9d9d9; line-height: 1.2; box-sizing: border-box; }
      .ts-time-picker-standalone .ant-time-picker-input:focus { outline: #0075b2 none 1px;  }
      .ts-time-picker-standalone .ant-time-picker-input:disabled { color: rgba(0,0,0,.25); background: #f5f5f5; cursor: not-allowed; }
      .ant-time-picker-panel { z-index: 999; min-width: 0; width: fit-content; }
      .ant-time-picker-panel-inner { background: #fff; border-radius: 3px; box-shadow: 0 2px 8px rgba(0,0,0,.15); padding: 0 0; overflow: hidden; width: fit-content; }
      .ant-time-picker-panel-combobox { display: flex; align-items: stretch; font-size: 18px; width: fit-content; }
      .ant-time-picker-panel-select { flex: 1; max-height: 200px; overflow-y: auto; border: none; scrollbar-width: none; -ms-overflow-style: none; }
      .ant-time-picker-panel-select[data-type="hour"],
      .ant-time-picker-panel-select[data-type="minute"] {
        flex: 0 0 ${HOUR_MINUTE_COLUMN_WIDTH_PX}px;
        min-width: ${HOUR_MINUTE_COLUMN_WIDTH_PX}px;
        max-width: ${HOUR_MINUTE_COLUMN_WIDTH_PX}px;
      }
      .ant-time-picker-panel-select[data-type="minute"]:not(.ts-minute-ten-outer) { max-height: none; overflow-y: visible; }
      .ant-time-picker-panel-select:not([data-is-ampm="true"]) { margin-top: 4px; }
      .ant-time-picker-panel-select:not([data-is-ampm="true"]):not(:last-child) { border-right: 1px solid #f0f0f0; }
      .ant-time-picker-panel-select::-webkit-scrollbar { display: none; }
      .ant-time-picker-panel-select ul { list-style: none; margin: 0; padding: 4px 0; }
      .ant-time-picker-panel-select li { padding: 4px 10px; cursor: pointer; text-align: center; font-size: 18px; }
      .ant-time-picker-panel-select[data-type="hour"] li {
        text-align: center !important;
        border-radius: 10px;
        margin-top: 2px;
        margin-bottom: 2px;
        margin-left: 8px;
        margin-right: 8px;
        transition: background-color 120ms ease;
      }
      .ant-time-picker-panel-select[data-type="minute"] li {
        border-radius: 10px;
        margin: 1px 8px;
        transition: background-color 120ms ease;
      }
      .ant-time-picker-panel-select[data-type="hour"] li:hover,
      .ant-time-picker-panel-select[data-type="minute"] li:hover {
        background-color: #dff0fb;
      }
      .ant-time-picker-panel-select[data-type="hour"] li.ant-time-picker-panel-cell-selected:hover,
      .ant-time-picker-panel-select[data-type="minute"] li.ant-time-picker-panel-cell-selected:hover {
        background-color: #dff0fb !important;
      }
      .ant-time-picker-panel-select[data-type="hour"] li.ant-time-picker-panel-cell-selected,
      .ant-time-picker-panel-select[data-type="minute"] li.ant-time-picker-panel-cell-selected {
        background-color: transparent;
      }
      .ant-time-picker-panel-select li.ant-time-picker-panel-cell-selected { color: inherit; font-weight: inherit; }
      .ant-time-picker-panel-select.ts-minute-ten-outer {
        overflow: visible;
        position: relative;
        min-height: 200px;
        max-height: 200px;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
      }
      .ts-minute-ten-scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: visible;
        overflow-x: visible;
        display: flex;
        flex-direction: column;
      }
      .ts-minute-ten-scroll::-webkit-scrollbar { display: none; }
      .ts-minute-ten-scroll ul.ts-minute-ten-list {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 100% !important;
        box-sizing: border-box;
        padding: ${MINUTE_TEN_LIST_PADDING_TOP_PX}px 0 ${MINUTE_TEN_LIST_PADDING_BOTTOM_PX}px 0;
        margin: 0;
        list-style: none;
      }
      .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row {
        height: ${MINUTE_TEN_ROW_HEIGHT_PX}px;
        min-height: ${MINUTE_TEN_ROW_HEIGHT_PX}px;
        max-height: ${MINUTE_TEN_ROW_HEIGHT_PX}px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 0 6px 0 2px;
        box-sizing: border-box;
        flex-shrink: 0;
      }
      .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row::after {
        content: '';
        flex-shrink: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 4px 0 4px 6px;
        border-color: transparent transparent transparent #bfbfbf;
        opacity: 0.9;
      }
      .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row:hover::after {
        border-left-color: #8c8c8c;
      }
      .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row.ant-time-picker-panel-cell-selected::after {
        border-left-color: #bfbfbf;
        opacity: 0.9;
      }
      /* Day-of-week: align day/hour rows (must follow default hour rules above) */
      .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"] li {
        height: ${DOW_DAY_ROW_HEIGHT_CALC};
        min-height: ${DOW_DAY_ROW_HEIGHT_CALC};
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 1px 8px;
        line-height: 1.2;
        box-sizing: border-box;
        flex-shrink: 0;
      }
      .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="hour"] li {
        height: ${DOW_HOUR_ROW_HEIGHT_CALC};
        min-height: ${DOW_HOUR_ROW_HEIGHT_CALC};
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 1px 8px;
        line-height: 1.2;
        box-sizing: border-box;
        flex-shrink: 0;
      }
      .ts-time-picker-with-dow .ts-minute-ten-scroll ul.ts-minute-ten-list li.ts-minute-ten-row {
        height: ${MINUTE_TEN_ROW_HEIGHT_PX}px;
        min-height: ${MINUTE_TEN_ROW_HEIGHT_PX}px;
        max-height: ${MINUTE_TEN_ROW_HEIGHT_PX}px;
        gap: 4px;
      }
      .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="hour"] ul,
      .ts-time-picker-with-dow .ant-time-picker-panel-select[data-type="day"] ul {
        padding: 4px 0;
        margin: 0;
      }
      .ts-time-picker-with-dow .ts-minute-ten-scroll ul.ts-minute-ten-list {
        padding: ${MINUTE_TEN_LIST_PADDING_TOP_PX}px 0 ${MINUTE_TEN_LIST_PADDING_BOTTOM_PX}px 0;
        margin: 0;
        justify-content: flex-start;
      }
      .ts-minute-subpop {
        display: none;
        z-index: 10060;
        background: #fff;
        border: 1px solid #e8e8e8;
        border-radius: 3px;
        box-shadow: 0 2px 10px rgba(0,0,0,.18);
        padding: 10px 12px;
        box-sizing: border-box;
        pointer-events: auto;
        overflow: visible;
      }
      .ts-minute-subpop-inner { display: flex; flex-direction: column; gap: 8px; align-items: stretch; }
      .ts-minute-subpop-row { display: flex; flex-wrap: nowrap; gap: 6px; justify-content: center; }
      .ts-minute-subpop-cell {
        min-width: 30px;
        text-align: center;
        cursor: pointer;
        font-size: 22px;
        padding: 4px 1px;
        border-radius: 2px;
        color: inherit;
        font-weight: inherit;
        line-height: 1.2;
        user-select: none;
      }
      .ts-minute-subpop-cell:hover {
        background-color: #dff0fb;
        border-radius: 4px;
      }
      .ts-minute-subpop-cell.ts-minute-subpop-cell-selected {
        color: inherit;
        font-weight: inherit;
      }
      .ts-minute-subpop.ts-minute-subpop--arrow-left::before {
        content: '';
        position: absolute;
        right: 100%;
        top: 50%;
        margin-right: -1px;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 8px 10px 8px 0;
        border-color: transparent #fff transparent transparent;
        z-index: 2;
        pointer-events: none;
      }
      .ts-minute-subpop.ts-minute-subpop--arrow-right::after {
        content: '';
        position: absolute;
        left: 100%;
        top: 50%;
        margin-left: 1px;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 8px 0 8px 10px;
        border-color: transparent transparent transparent #e8e8e8;
        pointer-events: none;
      }
      .ant-time-picker-panel-select[data-is-ampm="true"] { max-height: none; }
      .ant-time-picker-panel-addon {
        padding: 8px 12px;
        border-top: 1px solid #f0f0f0;
        text-align: center;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: center;
        gap: 12px;
        background-color:#dff0fb;
        font-weight: normal;
        border-bottom-left-radius: 3px;
        border-bottom-right-radius: 3px; }
      .ant-time-picker-panel-addon .ts-time-picker-now { color: #1890ff; cursor: pointer; }
      .ant-time-picker-panel-addon .ts-time-picker-ok { color: #1890ff; cursor: pointer; }
      .ant-time-picker-panel-addon .ts-time-picker-minute-toggle { color: #000; }
      .ant-time-picker-panel-addon .ts-time-picker-minute-toggle > span[data-step] {
        display: inline-block;
        padding: 2px 10px;
        border-radius: 9999px;
        line-height: 1.35;
        vertical-align: middle;
        font-size: inherit;
        box-sizing: border-box;
      }
      .ant-time-picker-panel-addon .ts-time-picker-minute-toggle > span[data-step]:not(.ts-time-picker-minute-link) {
        background: #1890ff;
        color: #fff;
        cursor: default;
        font-weight: bold;
      }
      .ant-time-picker-panel-addon .ts-time-picker-minute-toggle .ts-time-picker-minute-link {
        background: transparent;
        color: #1890ff;
        cursor: pointer;
      }

      .ts-time-picker-datetime-mod .ts-time-picker-timezone-abbr {
        font-size: 12px;
        color: #000;
        margin-left: 8px;
        white-space: nowrap;
        line-height: 1.2;
        box-sizing: border-box;
      }
    `;
    const style = document.createElement('style');
    style.id = 'ts-time-picker-styles';
    style.textContent = css + panelFontOverride + noSelectedHighlightCss;
    document.head.appendChild(style);
  }

  function scrollSelectedToTopOfList(container, listItem) {
    if (!container || !listItem) return;
    function applyScroll() {
      var cRect = container.getBoundingClientRect();
      var iRect = listItem.getBoundingClientRect();
      var nextTop = container.scrollTop + (iRect.top - cRect.top);
      var maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
      container.scrollTop = Math.max(0, Math.min(maxScroll, nextTop));
    }
    applyScroll();
    requestAnimationFrame(applyScroll);
  }

  /**
   * Primary-button mousedown on panel rows: cancel default so the focused hour/minute input does not
   * blur before the click handler runs (blur was resetting the list to the old value / top row).
   */
  function panelOptionMouseDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
  }

  /**
   * TsTimePicker_dateTimeMod constructor (DateTime mod: form inputs + TimeZoneAbbr + TimeOnChangeJS)
   * @param {Object} options
   * @param {HTMLElement} options.container - Element to mount the picker into
   * @param {string|Date} [options.value] - Initial time "HH:mm" or Date
   * @param {boolean} [options.use12Hours=true] - Use 12-hour format
   * @param {boolean} [options.useDurationHours=false] - Duration/time-length mode: hours 00–24, no meridian or time zone
   * @param {string} [options.format='h:mm A'] - Display format (only h:mm A supported for 12h)
   * @param {boolean} [options.disabled=false]
   * @param {boolean} [options.allowClear=false]
   * @param {boolean} [options.showNowButton=false] - Show "Now" link in footer (only when TimeZoneAbbr is set)
   * @param {Function} [options.onChange] - Callback(value) where value is "HH:mm"
   * @param {Function} [options.getPopupContainer] - Return element for dropdown (default: picker wrapper, scrolls with page; return document.body for viewport-fixed placement)
   * @param {boolean} [options.panelAlignLeft=false] - When true, panel left edge aligns to popup container (0px) instead of the input
   * @param {string} [options.placeholder='Select time']
   * @param {string} [options.name] - Input name attribute
   * @param {string} [options.className] - Extra class on wrapper
   * @param {number} [options.minuteStep=1] - 1 = show all 60 minutes (default), 5 = show 5-minute steps (00, 05, ..., 55)
   * @param {string} [options.HourName] - Name for hidden hour form field
   * @param {string} [options.HourValue] - Initial hour value (12-hour: 00-12, 00=midnight hour, 12=noon hour)
   * @param {string} [options.MinuteName] - Name for hidden minute form field
   * @param {string} [options.MinuteValue] - Initial minute value (0-59)
   * @param {string} [options.AMPMName] - Name for hidden meridian form field
   * @param {string} [options.AMPMValue] - Initial meridian "AM" or "PM"
   * @param {string} [options.TimeZoneAbbr] - Time zone abbreviation to display (e.g. "PST")
   * @param {string} [options.TimeOnChangeJS] - Callback expression when time changes (e.g. "onChangeStandard();")
   * @param {boolean} [options.showOkayButton=true] - Show "Okay" button in footer
   * @param {boolean} [options.lockPageScroll=false] - When true, lock host page scroll while panel is open (hides body scrollbar)
   * @param {boolean} [options.ShowDaysOfWeek=false] - Show day-of-week column (left) and trigger label; hides timezone and footer addon (Now/Okay)
   * @param {string} [options.DaysOfWeekName] - Name for hidden day-of-week form field (1–7)
   * @param {string} [options.DaysOfWeekValue] - Initial day-of-week value (1–7)
   * @param {string[]} [options.daysOfWeekLabels] - Optional localized day labels (Sun–Sat order)
   */
  function TsTimePicker_dateTimeMod(options) {
    options = options || {};
    this.version = "enbeded_TimePickerForDateTimeModV2.js_1.3.0";
    this.container = options.container;
    this.useDurationHours = options.useDurationHours === true;
    this.use12Hours = this.useDurationHours ? false : (options.use12Hours !== false);
    this.format = options.format || 'h:mm A';
    this.minuteStep = options.minuteStep !== undefined ? options.minuteStep : 1;
    this.disabled = !!options.disabled;
    this.allowClear = !!options.allowClear;
    this.showOkayButton = options.showOkayButton !== false;
    this.onChange = typeof options.onChange === 'function' ? options.onChange : function () {};
    this.getPopupContainer = typeof options.getPopupContainer === 'function'
      ? options.getPopupContainer
      : function () {
        return this._wrapper || document.body;
      }.bind(this);
    this.placeholder = options.placeholder != null ? options.placeholder : 'Select time';
    this.name = options.name || '';
    this.className = options.className || '';
    this.lockPageScroll = options.lockPageScroll === true;
    this.panelAlignLeft = options.panelAlignLeft === true;

    this.HourName = options.HourName != null ? String(options.HourName) : '';
    /** Matches window key from TimePickerJS.cfm: tsTimePicker_${HourName} */
    this._windowInstanceKey = this.HourName ? ('tsTimePicker_' + this.HourName) : '';
    this.HourValue = options.HourValue != null ? String(options.HourValue) : '';
    this.MinuteName = options.MinuteName != null ? String(options.MinuteName) : '';
    this.MinuteValue = options.MinuteValue != null ? String(options.MinuteValue) : '';
    this.AMPMName = options.AMPMName != null ? String(options.AMPMName) : '';
    this.AMPMValue = options.AMPMValue != null ? String(options.AMPMValue).toUpperCase() : '';
    this.TimeZoneAbbr = options.TimeZoneAbbr != null ? String(options.TimeZoneAbbr) : '';
    this.showNowButton = !!options.showNowButton && !!this.TimeZoneAbbr;
    this.TimeOnChangeJS = options.TimeOnChangeJS != null ? String(options.TimeOnChangeJS) : '';
    this.ShowDaysOfWeek = options.ShowDaysOfWeek === true;
    this.DaysOfWeekName = options.DaysOfWeekName != null ? String(options.DaysOfWeekName) : '';
    this.daysOfWeekLabels = Array.isArray(options.daysOfWeekLabels) ? options.daysOfWeekLabels.slice(0, 7) : null;
    this._dayOfWeek = parseDayOfWeekValue(options.DaysOfWeekValue);
    this._dayEl = null;
    this._hiddenDayOfWeek = null;

    if (this.useDurationHours) {
      this._state = parseDurationTime(options.value, this.minuteStep);
      if (this.HourValue !== '' || this.MinuteValue !== '') {
        this._state = parseDurationTimeFromForm(this.HourValue, this.MinuteValue, this.minuteStep);
      }
    } else {
      this._state = parseTime(options.value, this.minuteStep);
      if (this.HourValue !== '' || this.MinuteValue !== '' || this.AMPMValue !== '') {
        this._state = parseTimeFromForm(this.HourValue, this.MinuteValue, this.AMPMValue, this.minuteStep);
      }
    }
    this._wrapper = null;
    this._input = null;
    this._inputWrap = null;
    this._inputHour = null;
    this._inputMinute = null;
    this._meridianEl = null;
    /** In 12h split mode, hover-open attaches here (hour/minute/colon only), not meridian/timezone. */
    this._hmHoverZone = null;
    this._panel = null;
    this._hiddenHour = null;
    this._hiddenMinute = null;
    this._hiddenAMPM = null;
    this._boundClose = this._handleClickOutside.bind(this);
    this._hoverOpenTimer = null;
    this._boundWrapperMouseEnter = this._onWrapperMouseEnter.bind(this);
    this._boundWrapperMouseLeave = this._onWrapperMouseLeave.bind(this);
    this._boundInputWrapMouseEnter = this._onInputWrapMouseEnter.bind(this);
    this._boundInputWrapMouseLeave = this._onInputWrapMouseLeave.bind(this);
    this._boundInputWrapFocusOut = this._onInputWrapFocusOut.bind(this);
    this._boundPanelMouseEnter = this._onPanelMouseEnter.bind(this);
    this._boundPanelMouseLeave = this._onPanelMouseLeave.bind(this);
    this._panelDebugPinned = false;
    this._panelLeaveCloseTimer = null;
    this._panelPointerInside = false;
    this._id = 'ts-time-picker-' + Math.random().toString(36).slice(2, 11);
    /** Tracks in-panel pointer interactions so input-wrap blur doesn't close before selection click */
    this._pointerDownInPanel = false;
    /** AbortController for minute subpop scroll/resize listeners (cleared in _closePanel). */
    this._minutePopoutAbort = null;

    injectStyles();
    this._render();
  }

  TsTimePicker_dateTimeMod.prototype._render = function () {
    var self = this;
    const wrapper = document.createElement('div');
    wrapper.className = 'ant-time-picker ts-time-picker-standalone ts-time-picker-datetime-mod'
      + (this.useDurationHours ? ' ts-time-picker-duration' : '')
      + (this.ShowDaysOfWeek ? ' ts-time-picker-with-dow' : '')
      + (this.className ? ' ' + this.className : '');
    wrapper.setAttribute('data-ts-time-picker', this._id);
    wrapper.style.position = 'relative';

    if (this.HourName) {
      const hiddenHour = document.createElement('input');
      hiddenHour.type = 'hidden';
      hiddenHour.name = this.HourName;
      hiddenHour.id = this.HourName;
      hiddenHour.value = this._formHourValue();
      wrapper.appendChild(hiddenHour);
      this._hiddenHour = hiddenHour;
    }
    if (this.MinuteName) {
      const hiddenMinute = document.createElement('input');
      hiddenMinute.type = 'hidden';
      hiddenMinute.name = this.MinuteName;
      hiddenMinute.id = this.MinuteName;
      hiddenMinute.value = this._formMinuteValue();
      wrapper.appendChild(hiddenMinute);
      this._hiddenMinute = hiddenMinute;
    }
    if (this.AMPMName) {
      const hiddenAMPM = document.createElement('input');
      hiddenAMPM.type = 'hidden';
      hiddenAMPM.name = this.AMPMName;
      hiddenAMPM.id = this.AMPMName;
      hiddenAMPM.value = this._state.isPM ? 'PM' : 'AM';
      wrapper.appendChild(hiddenAMPM);
      this._hiddenAMPM = hiddenAMPM;
    }
    if (this.ShowDaysOfWeek && this.DaysOfWeekName) {
      const hiddenDay = document.createElement('input');
      hiddenDay.type = 'hidden';
      hiddenDay.name = this.DaysOfWeekName;
      hiddenDay.id = this.DaysOfWeekName;
      hiddenDay.value = String(this._dayOfWeek);
      wrapper.appendChild(hiddenDay);
      this._hiddenDayOfWeek = hiddenDay;
    }

    const inputWrap = document.createElement('div');
    inputWrap.className = 'ant-time-picker-input-wrap';
    if (this._windowInstanceKey) {
      inputWrap.classList.add('pw-' + this._windowInstanceKey);
    }
    if (this.ShowDaysOfWeek) {
      inputWrap.style.paddingRight = DOW_INPUT_WRAP_PADDING_RIGHT_PX + 'px';
    }
    this._inputWrap = inputWrap;

    if (this.useDurationHours) {
      const splitDuration = document.createElement('div');
      splitDuration.className = 'ts-time-picker-input-split';

      const hourInDuration = document.createElement('input');
      hourInDuration.type = 'text';
      hourInDuration.className = 'ts-time-picker-hour ant-time-picker-input';
      hourInDuration.setAttribute('inputmode', 'numeric');
      hourInDuration.setAttribute('maxlength', '2');
      hourInDuration.setAttribute('aria-label', 'Hours');
      hourInDuration.autocomplete = 'off';
      hourInDuration.value = formatHourDisplayDuration(this._state.hours24);

      const colonDuration = document.createElement('span');
      colonDuration.className = 'ts-time-picker-colon';
      colonDuration.textContent = ':';

      const minInDuration = document.createElement('input');
      minInDuration.type = 'text';
      minInDuration.className = 'ts-time-picker-minute ant-time-picker-input';
      minInDuration.setAttribute('inputmode', 'numeric');
      minInDuration.setAttribute('maxlength', '2');
      minInDuration.setAttribute('aria-label', 'Minutes');
      minInDuration.autocomplete = 'off';
      minInDuration.value = String(this._state.minutes).padStart(2, '0');

      if (this.disabled) {
        hourInDuration.disabled = true;
        minInDuration.disabled = true;
      }

      var hmHoverZoneDuration = document.createElement('div');
      hmHoverZoneDuration.className = 'ts-time-picker-hm-hover-zone';
      hmHoverZoneDuration.style.cssText = 'display:flex;align-items:center;flex-wrap:nowrap;gap:2px';
      hmHoverZoneDuration.appendChild(hourInDuration);
      hmHoverZoneDuration.appendChild(colonDuration);
      hmHoverZoneDuration.appendChild(minInDuration);
      splitDuration.appendChild(hmHoverZoneDuration);
      inputWrap.appendChild(splitDuration);

      this._input = null;
      this._inputHour = hourInDuration;
      this._inputMinute = minInDuration;
      this._meridianEl = null;
      this._hmHoverZone = hmHoverZoneDuration;

      var onDurationHourInput = function () {
        this._sanitizeSplitDigitInput(hourInDuration, 2);
        if (hourInDuration.value.length === 2) minInDuration.focus();
      }.bind(this);
      var onDurationMinInput = function () {
        this._sanitizeSplitDigitInput(minInDuration, 2);
      }.bind(this);

      var onSplitFieldFocus = function (inputEl) {
        return function () {
          if (!inputEl || typeof inputEl.select !== 'function') return;
          inputEl.select();
          self._openPanel(true);
        };
      };

      var onDurationSplitPointerDown = function (e) {
        if (e.button !== 0) return;
        this._openPanel(true);
      }.bind(this);

      hourInDuration.addEventListener('mousedown', onDurationSplitPointerDown);
      hourInDuration.addEventListener('focus', onSplitFieldFocus(hourInDuration));
      hourInDuration.addEventListener('click', onSplitFieldFocus(hourInDuration));
      hourInDuration.addEventListener('input', onDurationHourInput);
      hourInDuration.addEventListener('keydown', this._handleSplitHMKeyDown.bind(this, true));
      hourInDuration.addEventListener('blur', this._onSplitFieldBlur.bind(this));

      minInDuration.addEventListener('mousedown', onDurationSplitPointerDown);
      minInDuration.addEventListener('focus', onSplitFieldFocus(minInDuration));
      minInDuration.addEventListener('click', onSplitFieldFocus(minInDuration));
      minInDuration.addEventListener('input', onDurationMinInput);
      minInDuration.addEventListener('keydown', this._handleSplitHMKeyDown.bind(this, false));
      minInDuration.addEventListener('blur', this._onSplitFieldBlur.bind(this));

      this._applyDurationHour24Lock();
    } else if (this.use12Hours) {
      const split = document.createElement('div');
      split.className = 'ts-time-picker-input-split';

      const hourIn = document.createElement('input');
      hourIn.type = 'text';
      hourIn.className = 'ts-time-picker-hour ant-time-picker-input';
      hourIn.setAttribute('inputmode', 'numeric');
      hourIn.setAttribute('maxlength', '2');
      hourIn.setAttribute('aria-label', 'Hour');
      hourIn.autocomplete = 'off';
      hourIn.value = formatHourDisplay12(this._state);

      const colon = document.createElement('span');
      colon.className = 'ts-time-picker-colon';
      colon.textContent = ':';

      const minIn = document.createElement('input');
      minIn.type = 'text';
      minIn.className = 'ts-time-picker-minute ant-time-picker-input';
      minIn.setAttribute('inputmode', 'numeric');
      minIn.setAttribute('maxlength', '2');
      minIn.setAttribute('aria-label', 'Minutes');
      minIn.autocomplete = 'off';
      minIn.value = String(this._state.minutes).padStart(2, '0');

      const merEl = document.createElement('span');
      merEl.className = 'ts-time-picker-meridian ant-time-picker-input';
      merEl.setAttribute('role', 'button');
      merEl.tabIndex = this.disabled ? -1 : 0;
      merEl.setAttribute('aria-label', 'AM or PM');
      merEl.textContent = this._state.isPM ? 'PM' : 'AM';

      if (this.disabled) {
        hourIn.disabled = true;
        minIn.disabled = true;
        merEl.classList.add('ant-time-picker-input-disabled');
        merEl.setAttribute('aria-disabled', 'true');
      }

      var hmHoverZone = document.createElement('div');
      hmHoverZone.className = 'ts-time-picker-hm-hover-zone';
      hmHoverZone.style.cssText = 'display:flex;align-items:center;flex-wrap:nowrap;gap:2px';

      if (this.ShowDaysOfWeek) {
        const dayEl = document.createElement('span');
        dayEl.className = 'ts-time-picker-day';
        dayEl.setAttribute('aria-label', 'Day of week');
        dayEl.textContent = getDayLabel(this, this._dayOfWeek);
        if (this.disabled) {
          dayEl.classList.add('ts-time-picker-day-disabled');
        }
        hmHoverZone.appendChild(dayEl);
        this._dayEl = dayEl;
      }

      hmHoverZone.appendChild(hourIn);
      hmHoverZone.appendChild(colon);
      hmHoverZone.appendChild(minIn);
      split.appendChild(hmHoverZone);
      split.appendChild(merEl);
      if (this.TimeZoneAbbr && !this.ShowDaysOfWeek) {
        const tzAfterMeridian = document.createElement('span');
        tzAfterMeridian.className = 'ts-time-picker-timezone-abbr debug-tz-1';
        tzAfterMeridian.textContent = this.TimeZoneAbbr;
        split.appendChild(tzAfterMeridian);
      }
      inputWrap.appendChild(split);

      this._input = null;
      this._inputHour = hourIn;
      this._inputMinute = minIn;
      this._meridianEl = merEl;
      this._hmHoverZone = hmHoverZone;

      var onHourInput = function () {
        this._sanitizeSplitDigitInput(hourIn, 2);
        if (hourIn.value === '00') {
          hourIn.value = '12';
        }
        if (hourIn.value.length === 2) minIn.focus();
      }.bind(this);
      var onMinInput = function () {
        this._sanitizeSplitDigitInput(minIn, 2);
      }.bind(this);

      var onSplitFieldFocus = function (inputEl) {
        return function () {
          if (!inputEl || typeof inputEl.select !== 'function') return;
          inputEl.select();
          self._openPanel(true);
        };
      };

      // Click hour/minute to open the panel if closed (openOnly avoids toggle-close when already open).
      var onSplitTriggerPointerDown = function (e) {
        if (e.button !== 0) return;
        this._openPanel(true);
      }.bind(this);
      var handleMeridianFocus = function () {
        this._clearHoverOpenTimer();
        this._highlightMeridianValue();
      }.bind(this);

      hourIn.addEventListener('mousedown', onSplitTriggerPointerDown);
      hourIn.addEventListener('focus', onSplitFieldFocus(hourIn));
      hourIn.addEventListener('click', onSplitFieldFocus(hourIn));
      hourIn.addEventListener('input', onHourInput);
      hourIn.addEventListener('keydown', this._handleSplitHMKeyDown.bind(this, true));
      hourIn.addEventListener('blur', this._onSplitFieldBlur.bind(this));

      minIn.addEventListener('mousedown', onSplitTriggerPointerDown);
      minIn.addEventListener('focus', onSplitFieldFocus(minIn));
      minIn.addEventListener('click', onSplitFieldFocus(minIn));
      minIn.addEventListener('input', onMinInput);
      minIn.addEventListener('keydown', this._handleSplitHMKeyDown.bind(this, false));
      minIn.addEventListener('blur', this._onSplitFieldBlur.bind(this));

      merEl.addEventListener('click', function (e) {
        e.preventDefault();
        this._toggleMeridianFromUi();
      }.bind(this));
      merEl.addEventListener('focus', handleMeridianFocus);
      merEl.addEventListener('blur', this._onMeridianBlur.bind(this));
      merEl.addEventListener('keydown', this._handleMeridianKeyDown.bind(this));
      merEl.addEventListener('keyup', this._onMeridianKeyUp.bind(this));
    } else {
      const input = document.createElement('input');
      input.className = 'ant-time-picker-input';
      input.type = 'text';
      input.readOnly = false;
      input.placeholder = this.placeholder;
      if (this.name) input.name = this.name;
      if (this.disabled) input.disabled = true;
      input.value = toHHmm(this._state.hours24, this._state.minutes);

      inputWrap.appendChild(input);
      this._input = input;
      this._inputHour = null;
      this._inputMinute = null;
      this._meridianEl = null;
      this._hmHoverZone = null;

      input.addEventListener('mousedown', function (e) {
        if (e.button !== 0) return;
        this._openPanel(false);
      }.bind(this));
      input.addEventListener('focus', function () {
        this._openPanel(true);
      }.bind(this));
      input.addEventListener('input', this._handleTypedInput.bind(this));
      input.addEventListener('keydown', this._handleTypedKeyDown.bind(this));
      input.addEventListener('blur', this._commitTypedInput.bind(this));
      if (this.TimeZoneAbbr) {
        const tzAfterInput = document.createElement('span');
        tzAfterInput.className = 'ts-time-picker-timezone-abbr debug-tz-1';
        tzAfterInput.textContent = this.TimeZoneAbbr;
        inputWrap.appendChild(tzAfterInput);
      }
    }

    wrapper.appendChild(inputWrap);
    inputWrap.addEventListener('focusout', this._boundInputWrapFocusOut);
    inputWrap.addEventListener('mouseenter', this._boundInputWrapMouseEnter);
    inputWrap.addEventListener('mouseleave', this._boundInputWrapMouseLeave);

    this._wrapper = wrapper;

    var hoverOpenEl = this._hmHoverZone || wrapper;
    hoverOpenEl.addEventListener('mouseenter', this._boundWrapperMouseEnter);
    hoverOpenEl.addEventListener('mouseleave', this._boundWrapperMouseLeave);

    wrapper.addEventListener('mousedown', function (e) {
      if (this.lockPageScroll && this._isSplitTimeTarget(e.target) && (!this._panel || !this._panel.parentNode)) {
        this._lockPageScroll();
      }
    }.bind(this), true);

    if (this.container) {
      this.container.appendChild(wrapper);
    }
  };

  TsTimePicker_dateTimeMod.prototype._clearHoverOpenTimer = function () {
    if (this._hoverOpenTimer != null) {
      clearTimeout(this._hoverOpenTimer);
      this._hoverOpenTimer = null;
    }
  };

  TsTimePicker_dateTimeMod.prototype._clearPanelLeaveCloseTimer = function () {
    if (this._panelLeaveCloseTimer != null) {
      clearTimeout(this._panelLeaveCloseTimer);
      this._panelLeaveCloseTimer = null;
    }
  };

  /**
   * True when the pointer target is over the dropdown, form inputs, or visible minute subpop.
   * @param {Node|null} el
   * @returns {boolean}
   */
  TsTimePicker_dateTimeMod.prototype._shouldKeepPanelOpenForPointerTarget = function (el) {
    if (!el || el.nodeType !== 1) return false;
    if (this._panel && this._panel.contains(el)) return true;
    var wrap = this._inputWrap;
    if (wrap && wrap.contains(el)) return true;
    return false;
  };

  /**
   * True when the pointer is still over the dropdown or form inputs (CSS :hover).
   * Used when the leave-close timer fires so a stale _panelPointerInside flag cannot block close.
   * @returns {boolean}
   */
  TsTimePicker_dateTimeMod.prototype._isPointerOverKeepPanelOpenArea = function () {
    try {
      var wrap = this._inputWrap;
      if (wrap && wrap.matches(':hover')) return true;
      if (this._panel && this._panel.matches(':hover')) return true;
    } catch (err) { /* ignore */ }
    return false;
  };

  TsTimePicker_dateTimeMod.prototype._onPanelMouseEnter = function () {
    this._panelPointerInside = true;
    this._clearPanelLeaveCloseTimer();
  };

  TsTimePicker_dateTimeMod.prototype._onPanelMouseLeave = function (e) {
    this._panelPointerInside = false;
    var rel = e.relatedTarget;
    if (rel && this._shouldKeepPanelOpenForPointerTarget(rel)) return;
    this._schedulePanelCloseAfterLeave();
  };

  TsTimePicker_dateTimeMod.prototype._schedulePanelCloseAfterLeave = function () {
    if (this._panelDebugPinned) return;
    if (this.disabled) return;
    if (!this._panel || !this._panel.parentNode) return;
    this._clearPanelLeaveCloseTimer();
    var self = this;
    this._panelLeaveCloseTimer = setTimeout(function () {
      self._panelLeaveCloseTimer = null;
      if (!self._panel || !self._panel.parentNode) return;
      if (self._isPointerOverKeepPanelOpenArea()) return;
      self.onChange(self.getValue());
      self._closePanel();
    }, PANEL_MOUSE_LEAVE_CLOSE_DELAY_MS);
  };

  TsTimePicker_dateTimeMod.prototype._onInputWrapMouseEnter = function () {
    this._clearPanelLeaveCloseTimer();
  };

  TsTimePicker_dateTimeMod.prototype._onInputWrapMouseLeave = function (e) {
    if (this._panelDebugPinned) return;
    if (!this._panel || !this._panel.parentNode) return;
    var rel = e && e.relatedTarget;
    if (this._shouldKeepPanelOpenForPointerTarget(rel)) return;
    this._schedulePanelCloseAfterLeave();
  };

  /**
   * Close the dropdown when focus leaves the form inputs container (and is not moving into the panel).
   */
  TsTimePicker_dateTimeMod.prototype._onInputWrapFocusOut = function (e) {
    if (this._panelDebugPinned) return;
    if (!this._panel || !this._panel.parentNode) return;
    if (this._pointerDownInPanel) return;
    var related = e && e.relatedTarget;
    if (related && this._panel.contains(related)) return;
    var self = this;
    setTimeout(function () {
      if (!self._panel || !self._panel.parentNode) return;
      if (self._pointerDownInPanel) return;
      var active = document.activeElement;
      var wrap = self._inputWrap;
      if (wrap && active && wrap.contains(active)) return;
      if (self._panel && active && self._panel.contains(active)) return;
      self.onChange(self.getValue());
      self._closePanel();
    }, 0);
  };

  TsTimePicker_dateTimeMod.prototype._onWrapperMouseEnter = function () {
    if (this.disabled) return;
    this._clearPanelLeaveCloseTimer();
    if (this._panel && this._panel.parentNode) return;
    this._clearHoverOpenTimer();
    var self = this;
    this._hoverOpenTimer = setTimeout(function () {
      self._hoverOpenTimer = null;
      if (self.disabled) return;
      if (self._panel && self._panel.parentNode) return;
      self._openPanel(true);
    }, HOVER_OPEN_DELAY_MS);
  };

  TsTimePicker_dateTimeMod.prototype._onWrapperMouseLeave = function (e) {
    this._clearHoverOpenTimer();
    if (this._panelDebugPinned) return;
    if (!this._panel || !this._panel.parentNode) return;
    var rel = e && e.relatedTarget;
    if (this._shouldKeepPanelOpenForPointerTarget(rel)) return;
    this._schedulePanelCloseAfterLeave();
  };

  TsTimePicker_dateTimeMod.prototype._isSplitTimeTarget = function (el) {
    if (!el) return false;
    if (this._input && el === this._input) return true;
    if (this._inputHour && el === this._inputHour) return true;
    if (this._inputMinute && el === this._inputMinute) return true;
    if (this._meridianEl && el === this._meridianEl) return true;
    if (this._dayEl && el === this._dayEl) return true;
    return false;
  };

  TsTimePicker_dateTimeMod.prototype._sanitizeSplitDigitInput = function (inputEl, maxLen) {
    if (!inputEl) return;
    var d = String(inputEl.value).replace(/\D/g, '').slice(0, maxLen);
    if (d !== inputEl.value) inputEl.value = d;
  };

  TsTimePicker_dateTimeMod.prototype._toggleMeridianFromUi = function () {
    if (this.disabled || !this._meridianEl) return;
    var prev = this.getValue();
    this._state.isPM = !this._state.isPM;
    this._state.hours24 = this._state.isPM
      ? (this._state.hours12 === 12 ? 12 : this._state.hours12 + 12)
      : (this._state.hours12 === 12 ? 0 : this._state.hours12);
    this._syncInput();
    if (this.getValue() !== prev) {
      this.onChange(this.getValue());
    }
  };

  TsTimePicker_dateTimeMod.prototype._highlightMeridianValue = function () {
    if (!this._meridianEl || typeof window.getSelection !== 'function' || typeof document.createRange !== 'function') return;
    var selection = window.getSelection();
    if (!selection) return;
    var range = document.createRange();
    range.selectNodeContents(this._meridianEl);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  TsTimePicker_dateTimeMod.prototype._clearMeridianHighlight = function () {
    if (!this._meridianEl || typeof window.getSelection !== 'function') return;
    var selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    var range = selection.getRangeAt(0);
    if (!this._meridianEl.contains(range.commonAncestorContainer)) return;
    selection.removeAllRanges();
  };

  TsTimePicker_dateTimeMod.prototype._handleMeridianKeyDown = function (e) {
    if (this.disabled) return;
    var k = e.key;
    if (k === 'Enter') {
      e.preventDefault();
      this._commitSplitInputs();
      return;
    }
    if (k === ' ' || k === 'Spacebar') {
      e.preventDefault();
      this._toggleMeridianFromUi();
      return;
    }
    if (k === 'a' || k === 'A') {
      e.preventDefault();
      if (!this._state.isPM) return;
      var prevAmPm = this.getValue();
      this._state.isPM = false;
      this._state.hours24 = this._state.hours12 === 12 ? 0 : this._state.hours12;
      this._syncInput();
      if (this.getValue() !== prevAmPm) this.onChange(this.getValue());
      return;
    }
    if (k === 'p' || k === 'P') {
      e.preventDefault();
      if (this._state.isPM) return;
      var prevPm = this.getValue();
      this._state.isPM = true;
      this._state.hours24 = this._state.hours12 === 12 ? 12 : this._state.hours12 + 12;
      this._syncInput();
      if (this.getValue() !== prevPm) this.onChange(this.getValue());
    }
  };

  /**
   * Infer AM/PM from meridian field text (e.g. typed A/P/M) for contenteditable or transient input.
   * @returns {boolean|null} isPM, or null if not enough to commit
   */
  TsTimePicker_dateTimeMod.prototype._inferIsPMFromMeridianText = function (text) {
    var raw = String(text == null ? '' : text).replace(/\s+/g, '').toUpperCase();
    if (raw === '') return null;
    if (raw.indexOf('PM') === 0 || raw === 'P') return true;
    if (raw.indexOf('AM') === 0 || raw === 'A') return false;
    var c = raw.charAt(0);
    if (c === 'P') return true;
    if (c === 'A') return false;
    return null;
  };

  /**
   * After keyboard input in the meridian control, align hidden state and panel AM/PM column.
   */
  TsTimePicker_dateTimeMod.prototype._onMeridianKeyUp = function () {
    if (this.disabled || !this._meridianEl) return;
    var inferred = this._inferIsPMFromMeridianText(this._meridianEl.textContent);
    if (inferred !== null && inferred !== this._state.isPM) {
      var prevVal = this.getValue();
      this._state.isPM = inferred;
      this._state.hours24 = inferred
        ? (this._state.hours12 === 12 ? 12 : this._state.hours12 + 12)
        : (this._state.hours12 === 12 ? 0 : this._state.hours12);
      this._syncInput();
      if (this.getValue() !== prevVal) {
        this.onChange(this.getValue());
      }
    } else if (this._panel && this._panel.parentNode) {
      this._syncPanelAmPmSelectionFromState();
    }
  };

  TsTimePicker_dateTimeMod.prototype._handleSplitHMKeyDown = function (isHour, e) {
    if (this.disabled) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      this._commitSplitInputs();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length !== 1) return;
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * Commit split hour/minute fields on blur. When the user is clicking an option in the open
   * panel, blur often fires before the option's click — defer commit so the click updates state first.
   */
  TsTimePicker_dateTimeMod.prototype._onSplitFieldBlur = function () {
    var self = this;
    function run() {
      self._commitSplitInputs();
    }
    if (this._pointerDownInPanel) {
      setTimeout(run, 0);
    } else {
      run();
    }
  };

  TsTimePicker_dateTimeMod.prototype._applyDurationHour24Lock = function () {
    if (!this.useDurationHours || !this._inputMinute) return;
    var is24 = this._state.hours24 === 24;
    if (is24) {
      this._state.minutes = 0;
      this._inputMinute.value = '00';
    }
    if (this.disabled) return;
    this._inputMinute.disabled = is24;
    this._inputMinute.classList.toggle('ant-time-picker-input-disabled', is24);
    if (is24) {
      this._inputMinute.setAttribute('aria-disabled', 'true');
      this._inputMinute.title = '24 hours is the maximum entry for one day.';
    } else {
      this._inputMinute.removeAttribute('aria-disabled');
      this._inputMinute.removeAttribute('title');
    }
  };

  TsTimePicker_dateTimeMod.prototype._commitSplitInputs = function () {
    if (!this._inputHour || !this._inputMinute) return;
    var prevVal = this.getValue();
    var next;
    if (this.useDurationHours) {
      next = stateFromDurationSplitInputs(
        this._inputHour.value,
        this._inputMinute.value,
        this.minuteStep,
        this._state
      );
    } else {
      if (!this._meridianEl) return;
      var isPM = (this._meridianEl.textContent || '').trim().toUpperCase() === 'PM';
      next = stateFromSplitInputs(
        this._inputHour.value,
        this._inputMinute.value,
        isPM,
        this.minuteStep,
        this._state
      );
    }
    this._state = next;
    this._syncInput();
    if (this.getValue() !== prevVal) {
      this.onChange(this.getValue());
    }
    if (this._panel && this._panel.parentNode) {
      this._syncPanelHourMinuteListSelectionFromState();
    }
  };

  /**
   * When the panel is open, keep hour/minute column highlights in sync after typing in split fields (blur commit).
   */
  TsTimePicker_dateTimeMod.prototype._syncPanelHourMinuteListSelectionFromState = function () {
    if (!this._panel) return;
    var self = this;
    function applyColumn(type, dataValueStr) {
      var wrap = self._panel.querySelector('.ant-time-picker-panel-select[data-type="' + type + '"]');
      if (!wrap || wrap.hasAttribute('data-is-ampm')) return;
      wrap.querySelectorAll('li').forEach(function (n) {
        n.classList.remove('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
      });
      wrap.querySelectorAll('li[data-value="' + dataValueStr + '"]').forEach(function (n) {
        n.classList.add('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
      });
    }
    var hourVal = this.useDurationHours || !this.use12Hours
      ? String(this._state.hours24)
      : String(this._state.hours12);
    var minuteTen = String(Math.floor(this._state.minutes / 10) * 10);
    applyColumn('hour', hourVal);
    applyColumn('minute', minuteTen);
    this._scrollToSelected();
    this._syncPanelAmPmSelectionFromState();
  };

  /**
   * Keep the panel AM/PM column selection in sync with _state.isPM.
   */
  TsTimePicker_dateTimeMod.prototype._syncPanelAmPmSelectionFromState = function () {
    if (!this._panel || !this.use12Hours) return;
    var ampmSelect = this._panel.querySelector('.ant-time-picker-panel-select[data-is-ampm="true"]');
    if (!ampmSelect) return;
    var ul = ampmSelect.querySelector('ul');
    if (!ul) return;
    var amLi = ul.querySelector('li[data-ampm="am"]');
    var pmLi = ul.querySelector('li[data-ampm="pm"]');
    if (!amLi || !pmLi) return;
    var selectedLi = this._state.isPM ? pmLi : amLi;
    [amLi, pmLi].forEach(function (li) {
      li.classList.remove('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
    });
    selectedLi.classList.add('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
    if (selectedLi !== ul.firstElementChild) {
      ul.insertBefore(selectedLi, ul.firstChild);
    }
  };

  /**
   * Meridian blur: clear text selection only; panel closes on input-wrap focusout.
   */
  TsTimePicker_dateTimeMod.prototype._onMeridianBlur = function () {
    this._clearMeridianHighlight();
  };

  /**
   * Open the panel. When openOnly is true, no-op if already open (keyboard focus / tab).
   * When openOnly is false (default), toggles: close if already open.
   */
  TsTimePicker_dateTimeMod.prototype._openPanel = function (openOnly) {
    this._clearHoverOpenTimer();
    this._clearPanelLeaveCloseTimer();
    this._panelPointerInside = false;
    if (this.disabled) return;
    if (this._panel && this._panel.parentNode) {
      if (openOnly) return;
      this._closePanel();
      return;
    }
    if (this.lockPageScroll) this._lockPageScroll();
    this._panel = this._createPanel();
    var popupRoot = this.getPopupContainer();
    popupRoot.appendChild(this._panel);
    this._positionPanel();
    document.addEventListener('mousedown', this._boundClose);
    this._syncDowPanelColumnHeights();
    this._scrollToSelected();
    var self = this;
    requestAnimationFrame(function () {
      self._syncDowPanelColumnHeights();
      self._scrollToSelected();
    });
    this._attachHoursScrollLog();
  };

  /**
   * Day-of-week panels: sync column heights (day column uses DOW_DAY_COLUMN_HEIGHT_PX).
   */
  TsTimePicker_dateTimeMod.prototype._syncDowPanelColumnHeights = function () {
    if (!this.ShowDaysOfWeek || !this._panel) return;
    var h = DOW_PANEL_COLUMN_HEIGHT_PX + 'px';
    var dayH = DOW_DAY_COLUMN_HEIGHT_PX + 'px';
    var dayCol = this._panel.querySelector('.ant-time-picker-panel-select[data-type="day"]');
    var hourCol = this._panel.querySelector('.ant-time-picker-panel-select[data-type="hour"]');
    var minuteCol = this._panel.querySelector('.ant-time-picker-panel-select.ts-minute-ten-outer');
    if (dayCol) {
      dayCol.style.minHeight = dayH;
      dayCol.style.maxHeight = dayH;
      dayCol.style.paddingBottom = DOW_DAY_COLUMN_PADDING_BOTTOM_PX + 'px';
    }
    if (hourCol) {
      hourCol.style.minHeight = h;
      hourCol.style.maxHeight = h;
    }
    if (minuteCol) {
      minuteCol.style.minHeight = h;
      minuteCol.style.maxHeight = h;
    }
  };

  TsTimePicker_dateTimeMod.prototype._detachPanelAutoCloseForDebug = function () {
    this._clearPanelLeaveCloseTimer();
    this._clearHoverOpenTimer();
    document.removeEventListener('mousedown', this._boundClose);
    if (this._panel) {
      this._panel.removeEventListener('mouseenter', this._boundPanelMouseEnter);
      this._panel.removeEventListener('mouseleave', this._boundPanelMouseLeave);
    }
  };

  TsTimePicker_dateTimeMod.prototype._closePanel = function () {
    this._panelDebugPinned = false;
    this._clearPanelLeaveCloseTimer();
    this._panelPointerInside = false;
    if (this._minutePopoutAbort) {
      try {
        this._minutePopoutAbort.abort();
      } catch (e) { /* ignore */ }
      this._minutePopoutAbort = null;
    }
    if (this._hoursScrollLogCleanup) {
      this._hoursScrollLogCleanup();
    }
    if (this._panel && this._panel.parentNode) {
      this._panel.parentNode.removeChild(this._panel);
      this._panel = null;
    }
    document.removeEventListener('mousedown', this._boundClose);
    if (this.lockPageScroll) this._unlockPageScroll();
  };

  TsTimePicker_dateTimeMod.prototype._lockPageScroll = function () {
    if (this._scrollLocked) return;
    this._scrollLocked = true;
    var docEl = document.documentElement;
    var scrollbarWidth = window.innerWidth - docEl.clientWidth;
    this._prevScrollLock = {
      htmlOverflow: docEl.style.overflow,
      bodyPaddingRight: document.body.style.paddingRight
    };
    docEl.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = scrollbarWidth + 'px';
    }
  };

  TsTimePicker_dateTimeMod.prototype._unlockPageScroll = function () {
    if (!this._scrollLocked) return;
    this._scrollLocked = false;
    var docEl = document.documentElement;
    if (this._prevScrollLock) {
      docEl.style.overflow = this._prevScrollLock.htmlOverflow !== undefined ? this._prevScrollLock.htmlOverflow : '';
      document.body.style.paddingRight = this._prevScrollLock.bodyPaddingRight !== undefined ? this._prevScrollLock.bodyPaddingRight : '';
    }
    this._prevScrollLock = null;
  };

  TsTimePicker_dateTimeMod.prototype._handleClickOutside = function (e) {
    if (this._panelDebugPinned) return;
    if (!this._panel) return;
    var w = this._wrapper;
    var p = this._panel;
    if (w && w.contains(e.target)) return;
    if (p && p.contains(e.target)) return;
    this.onChange(this.getValue());
    this._closePanel();
  };

  TsTimePicker_dateTimeMod.prototype._positionPanel = function () {
    var anchor = this._inputWrap || this._input;
    if (!anchor || !this._panel) return;
    const rect = anchor.getBoundingClientRect();
    const gap = 4;
    const popupRoot = this.getPopupContainer();
    const isBody = popupRoot === document.body;
    this._panel.style.boxSizing = 'border-box';
    this._panel.style.width = 'fit-content';
    this._panel.style.minWidth = '0';
    this._panel.style.maxWidth = 'none';
    const panelHeight = this._panel.offsetHeight;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = panelHeight > 0 && spaceBelow < panelHeight + gap;
    if (isBody) {
      this._panel.style.position = 'fixed';
      this._panel.style.left = this.panelAlignLeft ? '0px' : (rect.left + 'px');
      this._panel.style.top = openAbove
        ? (rect.top - panelHeight - gap) + 'px'
        : (rect.bottom + gap) + 'px';
    } else {
      const rootRect = popupRoot.getBoundingClientRect();
      this._panel.style.position = 'absolute';
      this._panel.style.left = this.panelAlignLeft ? '0px' : ((rect.left - rootRect.left) + 'px');
      this._panel.style.top = openAbove
        ? (rect.top - rootRect.top - panelHeight - gap) + 'px'
        : (rect.bottom - rootRect.top + gap) + 'px';
    }
  };

  TsTimePicker_dateTimeMod.prototype._applyNowFromUserTime = function () {
    var self = this;
    fetchServerUserTime(function (err, userTime) {
      if (err || !userTime) {
        console.warn('TsTimePicker_dateTimeMod: failed to fetch server user time', err);
        return;
      }
      var hours24 = parseInt(userTime.H, 10);
      var minutes = Math.min(59, parseInt(userTime.M, 10));
      if (isNaN(hours24) || isNaN(minutes)) {
        console.warn('TsTimePicker_dateTimeMod: invalid USERTIME response', userTime);
        return;
      }
      self._state.hours24 = hours24;
      self._state.hours12 = hours24 % 12 || 12;
      self._state.minutes = minutes;
      self._state.isPM = hours24 >= 12;
      self._syncInput();
      self._closePanel();
      self.onChange(self.getValue());
    });
  };

  TsTimePicker_dateTimeMod.prototype._createPanel = function () {
    const panel = document.createElement('div');
    panel.className = 'ant-time-picker-panel' + (this.useDurationHours ? ' ts-time-picker-duration-panel' : '');
    panel.setAttribute('data-ts-time-picker-panel', this._id);
    panel.setAttribute('tabindex', '-1');

    panel.addEventListener('mouseenter', this._boundPanelMouseEnter);
    panel.addEventListener('mouseleave', this._boundPanelMouseLeave);

    panel.addEventListener('mousedown', function () {
      this._pointerDownInPanel = true;
      setTimeout(function () {
        this._pointerDownInPanel = false;
      }.bind(this), 0);
    }.bind(this), true);

    const inner = document.createElement('div');
    inner.className = 'ant-time-picker-panel-inner';
    inner.setAttribute('tabindex', '-1');

    const combobox = document.createElement('div');
    combobox.className = 'ant-time-picker-panel-combobox';
    combobox.setAttribute('tabindex', '-1');

    if (this.ShowDaysOfWeek) {
      combobox.appendChild(this._createDaySelect());
    }

    if (this.use12Hours) {
      const hourSelect = this._createSelect('hour', HOURS_12, this._state.hours12, function (val) {
        const h = parseInt(val, 10);
        const hours24 = this._state.isPM ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
        this._state.hours24 = hours24;
        this._state.hours12 = h;
        this._syncInput();
      }.bind(this));
      combobox.appendChild(hourSelect);
    } else if (this.useDurationHours) {
      const hourSelect = this._createSelect('hour', HOURS_DURATION, this._state.hours24, function (val) {
        var h = parseInt(val, 10);
        this._state.hours24 = h;
        this._state.hours12 = h % 12 || 12;
        this._state.isPM = false;
        if (h === 24) this._state.minutes = 0;
        this._syncInput();
      }.bind(this));
      combobox.appendChild(hourSelect);
    } else {
      const hours24 = Array.from({ length: 24 }, (_, i) => i);
      const hourSelect = this._createSelect('hour', hours24, this._state.hours24, function (val) {
        this._state.hours24 = parseInt(val, 10);
        this._state.hours12 = this._state.hours24 % 12 || 12;
        this._state.isPM = this._state.hours24 >= 12;
        this._syncInput();
      }.bind(this));
      combobox.appendChild(hourSelect);
    }

    const minuteSelect = this._createMinuteSelectTenWithPopout();
    combobox.appendChild(minuteSelect);

    inner.appendChild(combobox);

    if (!this.ShowDaysOfWeek) {
      const addon = document.createElement('div');
      addon.className = 'ant-time-picker-panel-addon';
      addon.setAttribute('tabindex', '-1');
      if (this.showNowButton) {
        const nowLink = document.createElement('span');
        nowLink.className = 'ts-time-picker-now';
        nowLink.textContent = translateOrFallback('JavaScript.Btn.Now', 'Now');
        nowLink.addEventListener('click', function () {
          this._applyNowFromUserTime();
        }.bind(this));
        addon.appendChild(nowLink);
      }
      if (this.showOkayButton) {
        const okLink = document.createElement('span');
        okLink.className = 'ts-time-picker-ok';
        okLink.textContent = 'Okay';
        okLink.addEventListener('click', function (e) {
          e.stopPropagation();
          this._closePanel();
          this.onChange(this.getValue());
        }.bind(this));
        addon.appendChild(okLink);
      }
      if (addon.childNodes.length > 0) {
        inner.appendChild(addon);
      }
    }

    panel.appendChild(inner);
    return panel;
  };

  TsTimePicker_dateTimeMod.prototype._createDaySelect = function () {
    const self = this;
    const wrap = document.createElement('div');
    wrap.className = 'ant-time-picker-panel-select ts-day-of-week-select';
    wrap.setAttribute('data-type', 'day');
    wrap.setAttribute('tabindex', '-1');
    const ul = document.createElement('ul');

    DAYS_OF_WEEK_NUMS.forEach(function (dayNum, index) {
      const li = document.createElement('li');
      li.textContent = getDayLabel(self, dayNum);
      li.setAttribute('tabindex', '-1');
      li.setAttribute('data-value', String(dayNum));
      if (dayNum === self._dayOfWeek) {
        li.classList.add('ant-time-picker-panel-cell-selected');
        li.classList.add('ant-time-picker-panel-select-option-selected');
      }
      li.addEventListener('mousedown', panelOptionMouseDown);
      li.addEventListener('click', function () {
        self._dayOfWeek = dayNum;
        wrap.querySelectorAll('li').forEach(function (n) {
          n.classList.remove('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
        });
        li.classList.add('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
        self._syncInput();
        if (self._hiddenDayOfWeek) {
          self._hiddenDayOfWeek.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      ul.appendChild(li);
    });

    wrap.appendChild(ul);
    return wrap;
  };

  TsTimePicker_dateTimeMod.prototype._syncPanelDaySelectionFromState = function () {
    if (!this._panel || !this.ShowDaysOfWeek) return;
    var picker = this;
    var wrap = this._panel.querySelector('.ant-time-picker-panel-select[data-type="day"]');
    if (!wrap) return;
    wrap.querySelectorAll('li').forEach(function (li) {
      var sel = parseInt(li.getAttribute('data-value'), 10) === picker._dayOfWeek;
      li.classList.toggle('ant-time-picker-panel-cell-selected', sel);
      li.classList.toggle('ant-time-picker-panel-select-option-selected', sel);
    });
  };

  TsTimePicker_dateTimeMod.prototype._createSelect = function (type, options, selectedValue, onSelect) {
    const self = this;
    const wrap = document.createElement('div');
    wrap.className = 'ant-time-picker-panel-select';
    wrap.setAttribute('data-type', type);
    wrap.setAttribute('tabindex', '-1');
    const ul = document.createElement('ul');

    if (type === 'hour') {
      ul.classList.add('pw-hour-selector');
      if (self._windowInstanceKey) {
        ul.classList.add('pw-' + self._windowInstanceKey);
      }
      options.forEach(function (opt) {
        const displayText = !self.use12Hours ? String(opt).padStart(2, '0') : String(opt);
        const dataVal = String(opt);
        const li = document.createElement('li');
        li.textContent = displayText;
        li.setAttribute('tabindex', '-1');
        li.setAttribute('data-value', dataVal);
        const current = self.use12Hours ? self._state.hours12 : self._state.hours24;
        const isSelected = (opt === current);
        if (isSelected) {
          li.classList.add('ant-time-picker-panel-cell-selected');
          li.classList.add('ant-time-picker-panel-select-option-selected');
        }
        li.addEventListener('mousedown', panelOptionMouseDown);
        li.addEventListener('click', function () {
          wrap.querySelectorAll('li').forEach(function (n) {
            n.classList.remove('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
          });
          li.classList.add('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
          self._suppressPanelScrollSync = true;
          scrollSelectedToTopOfList(wrap, li);
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              self._suppressPanelScrollSync = false;
            });
          });
          onSelect(dataVal);
        });
        ul.appendChild(li);
      });
    } else if (type === 'minute') {
      var currentTen = Math.floor(self._state.minutes / 10) * 10;
      options.forEach(function (opt) {
        const displayText = String(opt).padStart(2, '0');
        const dataVal = String(opt);
        const li = document.createElement('li');
        li.textContent = displayText;
        li.setAttribute('tabindex', '-1');
        li.setAttribute('data-value', dataVal);
        if (opt === currentTen) {
          li.classList.add('ant-time-picker-panel-cell-selected');
          li.classList.add('ant-time-picker-panel-select-option-selected');
        }
        li.addEventListener('mousedown', panelOptionMouseDown);
        li.addEventListener('click', function () {
          wrap.querySelectorAll('li').forEach(function (n) {
            n.classList.remove('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
          });
          li.classList.add('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
          self._suppressPanelScrollSync = true;
          scrollSelectedToTopOfList(wrap, li);
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              self._suppressPanelScrollSync = false;
            });
          });
          onSelect(dataVal);
          self.onChange(self.getValue());
        });
        ul.appendChild(li);
      });
    } else {
      options.forEach(function (opt) {
        const displayText = type === 'hour' && !self.use12Hours ? String(opt).padStart(2, '0') : String(opt);
        const dataVal = String(opt);
        const li = document.createElement('li');
        li.textContent = displayText;
        li.setAttribute('tabindex', '-1');
        li.setAttribute('data-value', dataVal);
        const current = self.use12Hours ? self._state.hours12 : self._state.hours24;
        const isSelected = (opt === current);
        if (isSelected) {
          li.classList.add('ant-time-picker-panel-cell-selected');
          li.classList.add('ant-time-picker-panel-select-option-selected');
        }
        li.addEventListener('mousedown', panelOptionMouseDown);
        li.addEventListener('click', function () {
          wrap.querySelectorAll('li').forEach(function (n) {
            n.classList.remove('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
          });
          li.classList.add('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
          if (!wrap.hasAttribute('data-is-ampm')) {
            self._suppressPanelScrollSync = true;
            scrollSelectedToTopOfList(wrap, li);
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                self._suppressPanelScrollSync = false;
              });
            });
          }
          onSelect(li.getAttribute('data-value'));
        });
        ul.appendChild(li);
      });
    }

    wrap.appendChild(ul);
    return wrap;
  };

  /**
   * Minute column: 00, 10, ... 50. Hovering a row opens exact minutes for that decade.
   */
  TsTimePicker_dateTimeMod.prototype._createMinuteSelectTenWithPopout = function () {
    const self = this;
    const outer = document.createElement('div');
    outer.className = 'ant-time-picker-panel-select ts-minute-ten-outer';
    outer.setAttribute('data-type', 'minute');
    outer.setAttribute('tabindex', '-1');

    const scrollInner = document.createElement('div');
    scrollInner.className = 'ts-minute-ten-scroll';

    const ul = document.createElement('ul');
    ul.classList.add('ts-minute-ten-list');
    ul.classList.add('pw-min_x10-selector');
    if (self._windowInstanceKey) {
      ul.classList.add('pw-' + self._windowInstanceKey);
    }

    const subpop = document.createElement('div');
    subpop.className = 'ts-minute-subpop';
    subpop.setAttribute('tabindex', '-1');
    subpop.setAttribute('role', 'menu');
    subpop.style.display = 'none';

    var hideTimer = null;
    var activeDecade = null;
    var anchorLi = null;

    function cancelHideSubpop() {
      if (hideTimer != null) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
    }

    function scheduleHideSubpop() {
      cancelHideSubpop();
      hideTimer = setTimeout(function () {
        hideTimer = null;
        subpop.style.display = 'none';
        activeDecade = null;
        anchorLi = null;
      }, MINUTE_SUBPOP_HIDE_DELAY_MS);
    }

    function syncMinuteTenListHighlight() {
      var baseTen = Math.floor(self._state.minutes / 10) * 10;
      outer.querySelectorAll('li[data-value]').forEach(function (n) {
        var v = parseInt(n.getAttribute('data-value'), 10);
        var sel = v === baseTen;
        n.classList.toggle('ant-time-picker-panel-cell-selected', sel);
        n.classList.toggle('ant-time-picker-panel-select-option-selected', sel);
      });
    }

    function syncSubpopCells() {
      subpop.querySelectorAll('.ts-minute-subpop-cell').forEach(function (cell) {
        var m = parseInt(cell.getAttribute('data-minute'), 10);
        cell.classList.toggle('ts-minute-subpop-cell-selected', m === self._state.minutes);
      });
    }

    function commitMinute(minuteValue) {
      self._state.minutes = minuteValue;
      self._syncInput();
      syncMinuteTenListHighlight();
      syncSubpopCells();
      cancelHideSubpop();
      subpop.style.display = 'none';
      activeDecade = null;
      anchorLi = null;
      self.onChange(self.getValue());
    }

    function showSubpopForDecade(decade, rowLi) {
      cancelHideSubpop();
      activeDecade = decade;
      anchorLi = rowLi;
      subpop.innerHTML = '';
      const inner = document.createElement('div');
      inner.className = 'ts-minute-subpop-inner';
      inner.classList.add('pw-min-subpop-selector');
      if (self._windowInstanceKey) {
        inner.classList.add('pw-' + self._windowInstanceKey);
      }
      const row1 = document.createElement('div');
      row1.className = 'ts-minute-subpop-row';
      const row2 = document.createElement('div');
      row2.className = 'ts-minute-subpop-row';

      for (var m = decade; m <= decade + 4; m++) {
        const cell = document.createElement('span');
        cell.className = 'ts-minute-subpop-cell';
        cell.setAttribute('data-minute', String(m));
        cell.setAttribute('role', 'menuitem');
        cell.textContent = String(m);
        if (m === self._state.minutes) cell.classList.add('ts-minute-subpop-cell-selected');
        cell.addEventListener('mousedown', panelOptionMouseDown);
        cell.addEventListener('click', function (e) {
          e.stopPropagation();
          commitMinute(parseInt(this.getAttribute('data-minute'), 10));
        });
        row1.appendChild(cell);
      }
      for (var m2 = decade + 5; m2 <= decade + 9; m2++) {
        const cell2 = document.createElement('span');
        cell2.className = 'ts-minute-subpop-cell';
        cell2.setAttribute('data-minute', String(m2));
        cell2.setAttribute('role', 'menuitem');
        cell2.textContent = String(m2);
        if (m2 === self._state.minutes) cell2.classList.add('ts-minute-subpop-cell-selected');
        cell2.addEventListener('mousedown', panelOptionMouseDown);
        cell2.addEventListener('click', function (e) {
          e.stopPropagation();
          commitMinute(parseInt(this.getAttribute('data-minute'), 10));
        });
        row2.appendChild(cell2);
      }

      inner.appendChild(row1);
      inner.appendChild(row2);
      subpop.appendChild(inner);
      positionMinuteSubpop(outer, rowLi, subpop);
    }

    function repositionSubpopIfVisible() {
      if (subpop.style.display !== 'block' || !anchorLi || activeDecade == null) return;
      positionMinuteSubpop(outer, anchorLi, subpop);
    }

    if (this._minutePopoutAbort) {
      try {
        this._minutePopoutAbort.abort();
      } catch (eAbort) { /* ignore */ }
    }
    this._minutePopoutAbort = new AbortController();
    var sig = this._minutePopoutAbort.signal;
    scrollInner.addEventListener('scroll', repositionSubpopIfVisible, { signal: sig });
    window.addEventListener('scroll', repositionSubpopIfVisible, { capture: true, signal: sig });
    window.addEventListener('resize', repositionSubpopIfVisible, { signal: sig });

    MINUTES_10.forEach(function (opt) {
      const displayText = String(opt).padStart(2, '0');
      const dataVal = String(opt);
      const li = document.createElement('li');
      li.classList.add('ts-minute-ten-row');
      li.textContent = displayText;
      li.setAttribute('tabindex', '-1');
      li.setAttribute('data-value', dataVal);
      var baseTen = Math.floor(self._state.minutes / 10) * 10;
      if (opt === baseTen) {
        li.classList.add('ant-time-picker-panel-cell-selected');
        li.classList.add('ant-time-picker-panel-select-option-selected');
      }
      li.addEventListener('mousedown', panelOptionMouseDown);
      li.addEventListener('mouseenter', function () {
        showSubpopForDecade(opt, li);
      });
      li.addEventListener('mouseleave', function (e) {
        var rel = e.relatedTarget;
        if (rel && (subpop === rel || subpop.contains(rel))) return;
        scheduleHideSubpop();
      });
      li.addEventListener('click', function () {
        var dv = li.getAttribute('data-value');
        outer.querySelectorAll('li').forEach(function (n) {
          n.classList.remove('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
        });
        li.classList.add('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
        self._suppressPanelScrollSync = true;
        scrollSelectedToTopOfList(scrollInner, li);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            self._suppressPanelScrollSync = false;
          });
        });
        commitMinute(parseInt(dv, 10));
      });
      ul.appendChild(li);
    });

    subpop.addEventListener('mouseenter', function () {
      cancelHideSubpop();
      self._clearPanelLeaveCloseTimer();
      self._panelPointerInside = true;
    });
    subpop.addEventListener('mouseleave', function (e) {
      self._panelPointerInside = false;
      var rel = e.relatedTarget;
      if (rel && anchorLi && (rel === anchorLi || anchorLi.contains(rel))) return;
      if (self._shouldKeepPanelOpenForPointerTarget(rel)) return;
      scheduleHideSubpop();
      self._schedulePanelCloseAfterLeave();
    });

    scrollInner.appendChild(ul);
    outer.appendChild(scrollInner);
    outer.appendChild(subpop);
    return outer;
  };

  TsTimePicker_dateTimeMod.prototype._formHourValue = function () {
    if (this.useDurationHours || !this.use12Hours) return String(this._state.hours24);
    return formatHourDisplay12(this._state);
  };

  TsTimePicker_dateTimeMod.prototype._formMinuteValue = function () {
    return String(this._state.minutes).padStart(2, '0');
  };

  TsTimePicker_dateTimeMod.prototype._syncHiddenInputs = function () {
    if (this._hiddenHour) this._hiddenHour.value = this._formHourValue();
    if (this._hiddenMinute) this._hiddenMinute.value = this._formMinuteValue();
    if (this._hiddenAMPM) this._hiddenAMPM.value = this._state.isPM ? 'PM' : 'AM';
    if (this._hiddenDayOfWeek) this._hiddenDayOfWeek.value = String(this._dayOfWeek);
  };

  TsTimePicker_dateTimeMod.prototype._fireTimeOnChangeJS = function () {
    if (!this.TimeOnChangeJS) return;
    try {
      if (typeof window !== 'undefined' && typeof window.eval === 'function') {
        window.eval(this.TimeOnChangeJS);
      }
    } catch (e) {
      console.warn('TsTimePicker_dateTimeMod TimeOnChangeJS error:', e);
    }
  };

  TsTimePicker_dateTimeMod.prototype._handleTypedInput = function () {
    if (!this._input) return;
    var cleaned = sanitizeTypedTimeInput(this._input.value);
    if (cleaned !== this._input.value) {
      this._input.value = cleaned;
    }
  };

  TsTimePicker_dateTimeMod.prototype._handleTypedKeyDown = function (e) {
    if (this.disabled) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      this._commitTypedInput();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length !== 1) return;
    if (!/[0-9:APMapm]/.test(e.key)) {
      e.preventDefault();
    }
  };

  TsTimePicker_dateTimeMod.prototype._commitTypedInput = function () {
    if (!this._input) return;
    var cleaned = sanitizeTypedTimeInput(this._input.value);
    if (cleaned !== this._input.value) {
      this._input.value = cleaned;
    }

    var parsed = parseTypedTimeInput(cleaned, this.use12Hours, this.minuteStep, this._state, this.useDurationHours);
    if (!parsed) {
      this._syncInput();
      return;
    }

    var previousValue = this.getValue();
    this._state.hours24 = parsed.hours24;
    this._state.hours12 = parsed.hours12;
    this._state.minutes = parsed.minutes;
    this._state.isPM = parsed.isPM;
    this._syncInput();

    if (this.getValue() !== previousValue) {
      this.onChange(this.getValue());
    }
  };

  TsTimePicker_dateTimeMod.prototype._syncInput = function () {
    if (this._inputHour && this._inputMinute && this.useDurationHours) {
      this._inputHour.value = formatHourDisplayDuration(this._state.hours24);
      this._inputMinute.value = String(this._state.minutes).padStart(2, '0');
      this._applyDurationHour24Lock();
    } else if (this._inputHour && this._inputMinute && this._meridianEl) {
      this._inputHour.value = formatHourDisplay12(this._state);
      this._inputMinute.value = String(this._state.minutes).padStart(2, '0');
      this._meridianEl.textContent = this._state.isPM ? 'PM' : 'AM';
      if (this._dayEl) {
        this._dayEl.textContent = getDayLabel(this, this._dayOfWeek);
      }
    } else if (this._input) {
      this._input.value = this.use12Hours
        ? format12h(this._state.hours24, this._state.minutes)
        : toHHmm(this._state.hours24, this._state.minutes);
    } else {
      return;
    }
    this._syncHiddenInputs();
    this._fireTimeOnChangeJS();
    if (this._panel && this._panel.parentNode) {
      if (this.ShowDaysOfWeek) {
        this._syncPanelDaySelectionFromState();
      }
      if (this.use12Hours) {
        this._syncPanelAmPmSelectionFromState();
      } else if (this.useDurationHours) {
        this._syncPanelHourMinuteListSelectionFromState();
      }
    }
  };

  /**
   * Nearest ancestor of start (up to but not including boundary) that can scroll vertically.
   */
  function findVerticalScrollableWithin(start, boundary) {
    var el = start;
    while (el && el !== boundary) {
      if (el.nodeType !== 1) {
        el = el.parentElement;
        continue;
      }
      var style = window.getComputedStyle(el);
      var oy = style.overflowY;
      if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && el.scrollHeight > el.clientHeight) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  TsTimePicker_dateTimeMod.prototype._scrollToSelected = function () {
    if (!this._panel) return;
    var picker = this;
    picker._suppressPanelScrollSync = true;
    try {
      picker._panel.querySelectorAll('.ant-time-picker-panel-select[data-type="hour"], .ant-time-picker-panel-select[data-type="minute"]').forEach(function (scrollContainer) {
        if (scrollContainer.hasAttribute('data-is-ampm')) return;
        var el = scrollContainer.querySelector('li.ant-time-picker-panel-cell-selected');
        if (!el) return;
        scrollSelectedToTopOfList(scrollContainer, el);
      });
    } finally {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          picker._suppressPanelScrollSync = false;
        });
      });
    }
  };

  TsTimePicker_dateTimeMod.prototype._attachHoursScrollLog = function () {
    if (!this._panel) return;
    var panel = this._panel;
    var onWheel = function (e) {
      if (!panel.contains(e.target)) return;
      var scrollable = findVerticalScrollableWithin(e.target, panel);
      if (!scrollable) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      var maxScroll = Math.max(0, scrollable.scrollHeight - scrollable.clientHeight);
      var nextTop = scrollable.scrollTop + e.deltaY;
      if (nextTop < 0) {
        e.preventDefault();
        e.stopPropagation();
        scrollable.scrollTop = 0;
        return;
      }
      if (nextTop > maxScroll) {
        e.preventDefault();
        e.stopPropagation();
        scrollable.scrollTop = maxScroll;
        return;
      }
    };
    panel.addEventListener('wheel', onWheel, { passive: false, capture: true });
    this._hoursScrollLogCleanup = function () {
      panel.removeEventListener('wheel', onWheel, { capture: true });
      this._hoursScrollLogCleanup = null;
    }.bind(this);
  };

  TsTimePicker_dateTimeMod.prototype.getValue = function () {
    return toHHmm(this._state.hours24, this._state.minutes);
  };

  TsTimePicker_dateTimeMod.prototype.setValue = function (value) {
    this._state = this.useDurationHours
      ? parseDurationTime(value, this.minuteStep)
      : parseTime(value, this.minuteStep);
    this._syncInput();
    this._syncHiddenInputs();
  };

  /**
   * Open the dropdown and pin it open for DevTools (no close on mouse leave or outside click).
   * @returns {HTMLElement|null} panel element
   */
  TsTimePicker_dateTimeMod.prototype.openPanelForDebug = function () {
    this._panelDebugPinned = true;
    if (this._panel && this._panel.parentNode) {
      this._syncDowPanelColumnHeights();
      this._detachPanelAutoCloseForDebug();
      return this._panel;
    }
    this._openPanel(true);
    this._detachPanelAutoCloseForDebug();
    return this._panel;
  };

  /**
   * Close a panel opened with openPanelForDebug and restore normal close behavior.
   */
  TsTimePicker_dateTimeMod.prototype.closePanelForDebug = function () {
    this._panelDebugPinned = false;
    this._closePanel();
  };

  TsTimePicker_dateTimeMod.prototype.setDisabled = function (disabled) {
    this.disabled = !!disabled;
    if (this._input) this._input.disabled = this.disabled;
    if (this._inputHour) this._inputHour.disabled = this.disabled;
    if (this._inputMinute) {
      if (this.disabled) {
        this._inputMinute.disabled = true;
      } else if (this.useDurationHours) {
        this._applyDurationHour24Lock();
      } else {
        this._inputMinute.disabled = false;
      }
    }
    if (this._meridianEl) {
      this._meridianEl.tabIndex = this.disabled ? -1 : 0;
      this._meridianEl.classList.toggle('ant-time-picker-input-disabled', this.disabled);
      if (this.disabled) {
        this._meridianEl.setAttribute('aria-disabled', 'true');
      } else {
        this._meridianEl.removeAttribute('aria-disabled');
      }
    }
    if (this._dayEl) {
      this._dayEl.classList.toggle('ts-time-picker-day-disabled', this.disabled);
    }
    if (this.disabled) {
      this._clearHoverOpenTimer();
    }
  };

  TsTimePicker_dateTimeMod.prototype.destroy = function () {
    this._clearHoverOpenTimer();
    this._clearPanelLeaveCloseTimer();
    var hoverOpenElDestroy = this._hmHoverZone || this._wrapper;
    if (hoverOpenElDestroy && this._boundWrapperMouseEnter) {
      hoverOpenElDestroy.removeEventListener('mouseenter', this._boundWrapperMouseEnter);
      hoverOpenElDestroy.removeEventListener('mouseleave', this._boundWrapperMouseLeave);
    }
    if (this._inputWrap && this._boundInputWrapFocusOut) {
      this._inputWrap.removeEventListener('focusout', this._boundInputWrapFocusOut);
    }
    if (this._inputWrap && this._boundInputWrapMouseEnter) {
      this._inputWrap.removeEventListener('mouseenter', this._boundInputWrapMouseEnter);
      this._inputWrap.removeEventListener('mouseleave', this._boundInputWrapMouseLeave);
    }
    this._closePanel();
    if (this._wrapper && this._wrapper.parentNode) {
      this._wrapper.parentNode.removeChild(this._wrapper);
    }
    this._wrapper = null;
    this._input = null;
    this._inputWrap = null;
    this._inputHour = null;
    this._inputMinute = null;
    this._meridianEl = null;
    this._dayEl = null;
    this._hiddenDayOfWeek = null;
    this._hmHoverZone = null;
    this._panel = null;
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = TsTimePicker_dateTimeMod;
  } else {
    global.TsTimePicker_dateTimeMod = TsTimePicker_dateTimeMod;
  }
})(typeof window !== 'undefined' ? window : this);
